import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Megaphone, FileStack, ShieldCheck, ArrowDownToLine,
  Star, Users2, ScrollText, Settings as SettingsIcon, LogOut, Menu, Plus, TrendingUp, Wallet, RotateCcw,
  FormInput, Image as ImageIcon, Download, Trash2, ArrowUp, ArrowDown, FileText, FileSpreadsheet, BarChart3, SlidersHorizontal,
  Database, Copy, Eye, Pause, Play, Link2, Mail, Target, Briefcase, KeyRound, MapPin, X, Repeat, Network as NetworkIcon, UserCog, LogIn, ChevronDown, ChevronRight,
} from "lucide-react";
const SEGMENTS = ["Loans", "Credit Card", "Demat & Investment", "Mutual Fund", "Insurance", "Savings Account", "Bill Pay & Recharge", "Shopping", "Other"];
import { Switch } from "@/components/ui/switch";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, inr, BACKEND_URL, getPublicSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatCard } from "@/components/StatCard";
import { ReferralTree } from "@/components/ReferralTree";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const SUB_STATUSES = ["new", "pending", "contacted", "verified", "converted", "approved", "rejected", "duplicate", "invalid", "closed"];
const CATEGORIES = ["Demat Account", "Savings Account", "Mutual Fund", "EMI Card", "Credit Card", "Personal Loan", "KYC/CIBIL Based Loan"];

function Table({ head, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-50 text-slate-400 text-xs uppercase">
            <tr>{head.map((h) => <th key={h} className="text-left p-3.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

async function downloadFile(fullUrl, filename) {
  try {
    const path = fullUrl.replace(`${BACKEND_URL}/api`, "");
    const r = await api.get(path, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Download failed. Please re-login and try again."); }
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Console Overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={d?.total_users ?? 0} icon={Users2} accent="navy" testid="admin-stat-users" />
        <StatCard label="New (7d)" value={d?.new_users ?? 0} icon={TrendingUp} accent="emerald" />
        <StatCard label="Applications" value={d?.applications ?? 0} icon={FileStack} accent="indigo" />
        <StatCard label="Conversions" value={d?.conversions ?? 0} icon={ShieldCheck} accent="emerald" />
        <StatCard label="Referral Rewards" value={inr(d?.referral_rewards)} icon={Wallet} accent="emerald" />
        <StatCard label="Wallet Liability" value={inr(d?.wallet_liability)} icon={Wallet} accent="amber" />
        <StatCard label="Active Campaigns" value={d?.active_campaigns ?? 0} icon={Megaphone} accent="navy" />
        <StatCard label="Pending KYC" value={d?.pending_kyc ?? 0} icon={ShieldCheck} accent="amber" testid="admin-stat-kyc" />
        <StatCard label="Pending Withdrawals" value={d?.pending_withdrawals ?? 0} icon={ArrowDownToLine} accent="rose" testid="admin-stat-wd" />
        <StatCard label="Pending Feedback" value={d?.pending_feedback ?? 0} icon={Star} accent="amber" />
      </div>
    </div>
  );
}

function Products() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { name: "", category: CATEGORIES[0], description: "", image: "", eligibility: "", features: "", documents: "", terms: "", cta: "Apply Now" };
  const [f, setF] = useState(blank);
  const [cats, setCats] = useState(CATEGORIES);
  const [newCat, setNewCat] = useState("");
  const load = () => api.get("/admin/products").then((r) => setList(r.data)).catch(() => {});
  const toggleStatus = async (p, status) => {
    try {
      await api.post(`/admin/products/${p.id}/status?status=${status}`);
      toast.success(status === "paused" ? "Product Pause/Hold kar diya" : "Product wapas Active ho gaya");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    getPublicSettings().then((d) => { if (d.product_categories?.length) setCats(d.product_categories); }).catch(() => {});
  }, []);
  const openEdit = (p) => { setEditId(p.id); setNewCat(""); setF({ name: p.name, category: p.category, description: p.description || "", image: p.image || "", eligibility: p.eligibility || "", features: (p.features || []).join("\n"), documents: (p.documents || []).join("\n"), terms: p.terms || "", cta: p.cta || "Apply Now" }); setOpen(true); };
  const save = async () => {
    const category = newCat.trim() || f.category;
    const payload = { ...f, category, features: (f.features || "").split("\n").map((s) => s.trim()).filter(Boolean), documents: (f.documents || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editId) { await api.put(`/admin/products/${editId}`, payload); toast.success("Product updated"); }
      else { await api.post("/admin/products", payload); toast.success("Product added"); }
      if (newCat.trim()) {
        const updated = [...cats, newCat.trim()];
        await api.post("/admin/settings", { product_categories: updated });
        setCats(updated); setNewCat("");
      }
      setOpen(false); setEditId(null); setF(blank); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Products</h1>
        <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: name / category…" data-testid="product-search-input" className="w-56 bg-white" />
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setF(blank); } }}>
          <DialogTrigger asChild><Button className="bg-[#0B223D] text-white font-bold rounded-xl" data-testid="admin-add-product-btn"><Plus className="w-4 h-4 mr-1.5" /> Add product</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{editId ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Product name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-form-name" />
              <div>
                <Label className="text-xs">Category (existing chunein ya nayi banayein)</Label>
                <select value={newCat ? "__new" : f.category} onChange={(e) => { if (e.target.value === "__new") { setNewCat(" "); setF({ ...f }); } else { setNewCat(""); setF({ ...f, category: e.target.value }); } }} data-testid="product-form-category" className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm">
                  {cats.map((c) => <option key={c}>{c}</option>)}
                  <option value="__new">+ Nayi category banayein…</option>
                </select>
                {newCat !== "" && <Input autoFocus placeholder="Nayi category ka naam (e.g. Gold Loan)" value={newCat.trim() ? newCat : ""} onChange={(e) => setNewCat(e.target.value)} data-testid="product-form-new-category" className="mt-2 h-10" />}
              </div>
              <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
              <Input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} />
              <div>
                <Label className="text-xs">Eligibility (one by one — har line me ek point)</Label>
                <Textarea rows={3} placeholder={"Age 18+\nValid PAN & Aadhaar\nIndian resident"} value={f.eligibility} onChange={(e) => setF({ ...f, eligibility: e.target.value })} data-testid="product-form-eligibility" />
              </div>
              <div>
                <Label className="text-xs">Features / Benefits (one by one — har line me ek point)</Label>
                <Textarea rows={3} placeholder={"Zero account opening fee\nInstant approval\n24x7 support"} value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} data-testid="product-form-features" />
              </div>
              <div>
                <Label className="text-xs">Documents required (one by one — har line me ek document)</Label>
                <Textarea rows={3} placeholder={"PAN\nGST\nBank statement"} value={f.documents} onChange={(e) => setF({ ...f, documents: e.target.value })} data-testid="product-form-documents" />
              </div>
              <div>
                <Label className="text-xs">Terms (one by one — har line me ek point)</Label>
                <Textarea rows={2} placeholder={"Reward credited on verified conversion\nT&C apply"} value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value })} data-testid="product-form-terms" />
              </div>
              <div><Label className="text-xs">Apply button label</Label><Input value={f.cta} onChange={(e) => setF({ ...f, cta: e.target.value })} data-testid="product-form-cta" placeholder="Apply Now" /></div>
              <Button onClick={save} className="w-full bg-[#0B223D] text-white font-bold" data-testid="product-form-save">{editId ? "Save changes" : "Save"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Table head={["Product", "Category", "Status", "Actions"]} testid="admin-products-table">
        {list.filter((p) => { const s = q.trim().toLowerCase(); return !s || [p.name, p.category].filter(Boolean).join(" ").toLowerCase().includes(s); }).map((p) => (
          <tr key={p.id} className="border-t border-slate-100">
            <td className="p-3.5 font-medium text-[#0B223D]">{p.name}</td>
            <td className="p-3.5 text-slate-500">{p.category}</td>
            <td className="p-3.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${p.status === "active" ? "bg-teal-50 text-teal-700" : p.status === "paused" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`product-status-${p.id}`}>{p.status}</span></td>
            <td className="p-3.5">
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} title="Edit product" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><SlidersHorizontal className="w-4 h-4" /></button>
                {p.status === "active"
                  ? <button onClick={() => toggleStatus(p, "paused")} data-testid={`product-pause-${p.id}`} title="Pause / Hold" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Pause className="w-4 h-4" /></button>
                  : <button onClick={() => toggleStatus(p, "active")} data-testid={`product-activate-${p.id}`} title="Activate" className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-600"><Play className="w-4 h-4" /></button>}
              </div>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Campaigns() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [forms, setForms] = useState([]);
  const blank = { name: "", product_id: "", category: "", description: "", payout_pool: 1000, payout_type: "flat", payout_percent: "", payout_slabs: [], form_id: "", desktop_creative: "", application_url: "", tracking_url: "", direct_url: "", custom_params: "", partner_name: "", po_amount: 0, kpi: "", notes: "", flow: "", apply_mode: "redirect", tracking_type: "online", high_payout: false, same_day_payout: false, top_converting: false, lead_track_enabled: false, lead_track_percent: "", one_ip_one_lead: false, start_date: "", end_date: "", staff_label: "", payment_frequency: "", settlement_terms: "", payment_timeline: "", min_billable_qty: "", approval_conditions: "", holding_period: "", payment_notes: "" };
  const [f, setF] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [segs, setSegs] = useState(SEGMENTS);
  const load = () => api.get("/campaigns").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); api.get("/admin/forms").then((r) => setForms(r.data)).catch(() => {}); api.get("/segments").then((r) => setSegs(r.data.segments)).catch(() => {}); }, []);
  const addSegment = async () => {
    const name = window.prompt("Naya segment ka naam daalein:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/admin/segments", { name: name.trim() });
      setSegs(data.segments);
      setF((prev) => ({ ...prev, category: data.name }));
      toast.success(`Segment "${data.name}" add ho gaya`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openEdit = (c) => {
    setEditId(c.id);
    setF({ ...blank, ...c, payout_pool: c.payout_pool ?? 1000, po_amount: c.po_amount ?? 0, flow: (c.flow || []).join("\n"), lead_track_enabled: c.lead_track_percent != null, lead_track_percent: c.lead_track_percent ?? "" });
    setOpen(true);
  };
  const save = async () => {
    const payload = { ...f, payout_pool: Number(f.payout_pool), payout_percent: f.payout_type === "percent" && f.payout_percent !== "" ? Number(f.payout_percent) : null, payout_slabs: (f.payout_slabs || []).map((s) => ({ min_conversions: Number(s.min_conversions) || 0, payout: Number(s.payout) || 0 })).filter((s) => s.payout > 0 && s.min_conversions > 0), po_amount: Number(f.po_amount) || 0, min_billable_qty: f.min_billable_qty === "" || f.min_billable_qty == null ? null : Number(f.min_billable_qty), min_payout: 0, max_payout: Number(f.payout_pool), mobile_creative: f.desktop_creative, top_converting: !!f.top_converting, lead_track_percent: f.lead_track_enabled && f.lead_track_percent !== "" ? Math.min(100, Math.max(0, Number(f.lead_track_percent))) : null, flow: (f.flow || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editId) { await api.put(`/admin/campaigns/${editId}`, payload); toast.success("Campaign updated"); }
      else { await api.post("/admin/campaigns", payload); toast.success("Campaign created with auto Campaign ID (CKC)"); }
      setOpen(false); setEditId(null); setF(blank); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleStatus = async (c, status) => {
    try { await api.put(`/admin/campaigns/${c.id}`, { ...c, status }); toast.success(`Campaign ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteCampaign = async (c) => {
    if (!window.confirm(`"${c.name}" campaign DELETE karein? Iske referral links bhi delete ho jayenge. Leads/customer data safe rahega.`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Campaign deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const copyLink = (c) => {
    if (!c.campaign_code) { toast.error("Campaign ID missing hai — Edit karke Save karein, ID auto-generate ho jayegi."); return; }
    const base = window.location.origin;
    const url = `${base}/apply/${c.campaign_code}`;
    navigator.clipboard.writeText(url); toast.success("Campaign link copied!");
  };
  const previewLink = (c) => {
    if (!c.campaign_code) { toast.error("Campaign ID missing hai — Edit karke Save karein, ID auto-generate ho jayegi."); return; }
    window.open(`${window.location.origin}/apply/${c.campaign_code}`, "_blank");
  };
  const emailAll = async (c) => {
    if (!window.confirm(`Email all users about "${c.name}"?`)) return;
    try { const { data } = await api.post(`/admin/campaigns/${c.id}/broadcast-email`); toast.success(`Emailed ${data.emailed}/${data.total} users`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [pinDlg, setPinDlg] = useState(null);
  const [pinMode, setPinMode] = useState("all");
  const [pinText, setPinText] = useState("");
  const [pinCount, setPinCount] = useState(0);
  const openPins = async (c) => {
    setPinDlg(c);
    try { const { data } = await api.get(`/admin/campaigns/${c.id}/pincodes`); setPinMode(data.mode); setPinCount(data.count); setPinText((data.pincodes || []).join("\n")); }
    catch (e) { setPinMode(c.pincode_mode || "all"); setPinText(""); setPinCount(0); }
  };
  const savePins = async () => {
    try { const { data } = await api.post(`/admin/campaigns/${pinDlg.id}/pincodes`, { mode: pinMode, pincodes_text: pinText }); setPinCount(data.count); toast.success(`Saved: ${data.count} pincodes (${data.mode} mode)`); setPinDlg(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const onPinFile = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setPinText(String(r.result || "")); r.readAsText(file); };
  const [uploading, setUploading] = useState(false);
  const uploadCreative = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 MB image");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, desktop_creative: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const [showFB, setShowFB] = useState(false);
  const [fbName, setFbName] = useState("");
  const [fbFields, setFbFields] = useState([]);
  const [fbEditId, setFbEditId] = useState(null);
  const [fbCustom, setFbCustom] = useState({ label: "", type: "text", required: false, options: "" });
  const fbToggleQuick = (type, label, req) => {
    const idx = fbFields.findIndex((x) => x.type === type && x.label === label);
    if (idx >= 0) { setFbFields(fbFields.filter((_, j) => j !== idx)); return; }
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    setFbFields([...fbFields, { type, label, key, required: req }]);
  };
  const fbAddCustom = () => {
    const label = fbCustom.label.trim();
    if (!label) return toast.error("Field label daalein");
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    setFbFields([...fbFields, { type: fbCustom.type, label, key, required: fbCustom.required,
      ...(fbCustom.type === "select" ? { options: fbCustom.options.split(",").map((s) => s.trim()).filter(Boolean) } : {}) }]);
    setFbCustom({ label: "", type: "text", required: false, options: "" });
  };
  const fbEditLabel = (i, v) => setFbFields(fbFields.map((x, j) => (j === i ? { ...x, label: v } : x)));
  const fbToggleReq = (i) => setFbFields(fbFields.map((x, j) => (j === i ? { ...x, required: !x.required } : x)));
  const fbRemove = (i) => setFbFields(fbFields.filter((_, j) => j !== i));
  const fbMove = (i, dir) => { const j = i + dir; if (j < 0 || j >= fbFields.length) return; const a = [...fbFields]; [a[i], a[j]] = [a[j], a[i]]; setFbFields(a); };
  const editAttachedForm = async () => {
    if (!f.form_id) return;
    try {
      const { data } = await api.get(`/admin/forms/${f.form_id}`);
      setFbName(data.name); setFbFields(data.fields || []); setFbEditId(f.form_id); setShowFB(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createAndAttachForm = async () => {
    if (!fbName.trim() || !fbFields.length) return toast.error("Form name aur kam se kam 1 field chahiye");
    const wasEdit = !!fbEditId;
    try {
      let formId = fbEditId;
      if (!formId) {
        await api.post("/admin/forms", { name: fbName.trim() });
        const { data: fl } = await api.get("/admin/forms");
        const created = fl.filter((x) => x.name === fbName.trim()).pop();
        if (!created) throw new Error("form not created");
        formId = created.id;
      } else {
        await api.put(`/admin/forms/${formId}`, { name: fbName.trim() });
      }
      await api.post(`/admin/forms/${formId}/version`, { fields: fbFields, publish: true });
      const { data: fl2 } = await api.get("/admin/forms");
      setForms(fl2);
      setF({ ...f, form_id: formId });
      setShowFB(false); setFbName(""); setFbFields([]); setFbEditId(null);
      toast.success(wasEdit ? "Form updated (new version) & attached!" : "Form created, published & attached!");
    } catch (e) { toast.error(apiError(e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Campaign Link Management</h1>
        <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: name / ID / label…" data-testid="campaign-search-input" className="w-56 bg-white" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#0B223D] text-white font-bold rounded-xl" data-testid="admin-add-campaign-btn"><Plus className="w-4 h-4 mr-1.5" /> Create Campaign Link</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{editId ? "Edit Campaign" : "Create Campaign"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="campaign-form-name" />
              <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 space-y-1">
                <Label className="text-xs font-bold text-sky-800">Internal Staff Label (sirf staff ko dikhega, customer ko NAHI)</Label>
                <select value={f.staff_label} onChange={(e) => setF({ ...f, staff_label: e.target.value })} data-testid="campaign-form-staff-label" className="w-full h-10 rounded-lg border border-sky-200 px-3 text-sm bg-white">
                  <option value="">— No label —</option>
                  <option value="Link 1">Link 1</option>
                  <option value="Link 2">Link 2</option>
                  <option value="Link 3">Link 3</option>
                  <option value="Link 4">Link 4</option>
                  <option value="Link 5">Link 5</option>
                </select>
                <p className="text-[11px] text-sky-700">Staff internally campaign ko Link 1/2/3 se identify kar sakte hain. Ye label public pages/customers ko kabhi nahi dikhta.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Segment</Label>
                  <button type="button" onClick={addSegment} data-testid="campaign-add-segment-btn" className="text-xs font-bold text-teal-700 hover:text-teal-800">+ Add new segment</button>
                </div>
                <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, product_id: "" })} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="campaign-form-segment"><option value="">— Select segment —</option>{segs.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              </div>
              <div>
                <Label className="text-xs">Attach Dynamic Form</Label>
                <select value={f.form_id} onChange={(e) => setF({ ...f, form_id: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="campaign-form-formid"><option value="">— No form (basic fields) —</option>{forms.filter((x) => x.published_version).map((x) => <option key={x.id} value={x.id}>{x.name} (v{x.published_version})</option>)}</select>
              </div>
              <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <button type="button" onClick={() => { setShowFB(!showFB); if (showFB) { setFbEditId(null); setFbFields([]); setFbName(""); } }} data-testid="campaign-inline-form-toggle" className="text-xs font-bold text-teal-700 hover:text-teal-800">+ Create Dynamic Form — yahi se jo fields chahiye wo add karein</button>
                  {f.form_id && <button type="button" onClick={editAttachedForm} data-testid="campaign-inline-form-edit" className="text-xs font-bold text-indigo-600 hover:text-indigo-800">✎ Edit attached form</button>}
                </div>
                {showFB && (
                  <div className="space-y-3">
                    <Input placeholder="Form name (e.g. Loan Apply Form)" value={fbName} onChange={(e) => setFbName(e.target.value)} data-testid="inline-form-name" className="h-10" />
                    <div className="flex flex-wrap gap-1.5">
                      {QUICK_FIELDS.map(([type, label, req]) => {
                        const on = fbFields.some((x) => x.type === type && x.label === label);
                        return <button key={type} type="button" onClick={() => fbToggleQuick(type, label, req)} data-testid={`inline-field-${type}`}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label}</button>;
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-lg p-2">
                      <Input placeholder="Custom field label (jo bhi chahiye)" value={fbCustom.label} onChange={(e) => setFbCustom({ ...fbCustom, label: e.target.value })} data-testid="inline-custom-label" className="h-9 flex-1 min-w-[150px] text-xs" />
                      <select value={fbCustom.type} onChange={(e) => setFbCustom({ ...fbCustom, type: e.target.value })} data-testid="inline-custom-type" className="h-9 rounded-lg border border-slate-200 px-2 text-xs bg-white">
                        <option value="text">Text</option><option value="number">Number</option><option value="email">Email</option><option value="mobile">Mobile</option><option value="date">Date</option><option value="select">Dropdown</option><option value="textarea">Textarea</option><option value="consent">Checkbox</option>
                      </select>
                      <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={fbCustom.required} onChange={(e) => setFbCustom({ ...fbCustom, required: e.target.checked })} data-testid="inline-custom-required" /> Required</label>
                      <Button type="button" size="sm" onClick={fbAddCustom} data-testid="inline-custom-add" className="h-9 bg-[#0B223D] text-white text-xs">+ Add</Button>
                      {fbCustom.type === "select" && <Input placeholder="Dropdown options (comma separated)" value={fbCustom.options} onChange={(e) => setFbCustom({ ...fbCustom, options: e.target.value })} data-testid="inline-custom-options" className="h-9 w-full text-xs" />}
                    </div>
                    {fbFields.length > 0 && (
                      <div className="space-y-1.5" data-testid="inline-form-fields-list">
                        {fbFields.map((x, i) => (
                          <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                            <span className="text-[10px] uppercase text-slate-400 w-14 shrink-0">{x.type}</span>
                            <Input value={x.label} onChange={(e) => fbEditLabel(i, e.target.value)} data-testid={`inline-field-label-${i}`} className="h-8 text-xs flex-1" />
                            <label className="flex items-center gap-1 text-[10px] text-slate-500 shrink-0"><input type="checkbox" checked={!!x.required} onChange={() => fbToggleReq(i)} data-testid={`inline-field-req-${i}`} /> Req</label>
                            <button type="button" onClick={() => fbMove(i, -1)} className="text-slate-400 hover:text-slate-700 text-xs px-1" title="Move up">↑</button>
                            <button type="button" onClick={() => fbMove(i, 1)} className="text-slate-400 hover:text-slate-700 text-xs px-1" title="Move down">↓</button>
                            <button type="button" onClick={() => fbRemove(i)} data-testid={`inline-field-remove-${i}`} className="text-rose-400 hover:text-rose-600 text-xs px-1" title="Remove">✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button type="button" onClick={createAndAttachForm} data-testid="inline-form-save" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-10">{fbEditId ? "Update form (new version) & attach" : "Save form & attach to campaign"}</Button>
                  </div>
                )}
              </div>
              <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
              <div>
                <Label className="text-xs">Custom params (optional — brand link me pass honge, report me bhi dikhenge)</Label>
                <Input placeholder="sub_id=ck&source=web" value={f.custom_params} onChange={(e) => setF({ ...f, custom_params: e.target.value })} data-testid="campaign-form-params" className="font-mono-num text-xs" />
              </div>
              <div>
                <Label className="text-xs">Brand Link (application URL — redirect after submit)</Label>
                <Input placeholder="https://brand.com/apply (isme {user_id} ya params laga sakte hain)" value={f.application_url} onChange={(e) => setF({ ...f, application_url: e.target.value })} data-testid="campaign-form-appurl" />
                <p className="text-[10px] text-slate-400 mt-1">Isi ek link par user redirect hoga. Har lead ki unique Client UID auto-pass hogi (format: 1 letter + 5 digits, jaise K48291). Placeholder: {"{user_id}"}</p>
              </div>
              <div>
                <Label className="text-xs">Campaign image (upload karein — link nahi)</Label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={uploadCreative} data-testid="campaign-form-image-upload" className="text-xs text-slate-500 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#0B223D] file:text-white file:text-xs file:font-semibold" />
                  {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
                </div>
                <p className="text-[11px] text-slate-400 mt-1" data-testid="creative-size-hint">Recommended banner size: <b>1200×600 px (2:1 ratio)</b>, PNG/WebP, under 500 KB — is size me image screen par perfect fit hogi, bina crop/side-bars ke.</p>
                {f.desktop_creative && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={f.desktop_creative.startsWith("/api/") ? `${BACKEND_URL}${f.desktop_creative}` : f.desktop_creative} alt="creative" className="h-14 rounded-lg border border-slate-200 object-cover" data-testid="campaign-form-image-preview" />
                    <button type="button" onClick={() => setF({ ...f, desktop_creative: "" })} className="text-xs text-rose-500">Remove</button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Apply mode</Label><select value={f.apply_mode} onChange={(e) => setF({ ...f, apply_mode: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm" data-testid="campaign-form-applymode"><option value="redirect">Link redirect (no form)</option><option value="form">Collect form</option></select></div>
                <div><Label className="text-xs">Lead tracking</Label><select value={f.tracking_type} onChange={(e) => setF({ ...f, tracking_type: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm" data-testid="campaign-form-tracking"><option value="online">Online (user can check by mobile)</option><option value="offline">Offline (no status check)</option></select></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Offer start date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="h-10" /></div>
                <div><Label className="text-xs">Offer end date</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="h-10" data-testid="campaign-form-enddate" /></div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.high_payout} onChange={(e) => setF({ ...f, high_payout: e.target.checked })} data-testid="campaign-form-highpayout" /> High Payout</label>
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.same_day_payout} onChange={(e) => setF({ ...f, same_day_payout: e.target.checked })} data-testid="campaign-form-sameday" /> Same Day Payout</label>
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.top_converting} onChange={(e) => setF({ ...f, top_converting: e.target.checked })} data-testid="campaign-form-topconverting" /> Top Converting</label>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.lead_track_enabled} onChange={(e) => setF({ ...f, lead_track_enabled: e.target.checked })} data-testid="campaign-form-leadtrack-toggle" /> Lead Track % badge dikhaye (optional)</label>
                {f.lead_track_enabled && <Input type="number" min={0} max={100} value={f.lead_track_percent} onChange={(e) => setF({ ...f, lead_track_percent: e.target.value })} placeholder="e.g. 85" data-testid="campaign-form-leadtrack" className="h-10" />}
              </div>
              <div className="rounded-xl border border-slate-200 p-3 space-y-1">
                <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.one_ip_one_lead} onChange={(e) => setF({ ...f, one_ip_one_lead: e.target.checked })} data-testid="campaign-form-one-ip" /> 1 IP / Device = 1 Lead</label>
                <p className="text-[11px] text-slate-400">Same customer dobara apply kar sakta hai, lekin same device/network se do alag log apply karein to block hoga.</p>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2">
                <p className="text-xs font-bold text-amber-800">Admin/Partner only — NEVER shown to customers</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Partner name" value={f.partner_name} onChange={(e) => setF({ ...f, partner_name: e.target.value })} />
                  <Input type="number" placeholder="PO / Payout (₹)" value={f.po_amount} onChange={(e) => setF({ ...f, po_amount: e.target.value })} data-testid="campaign-form-po" />
                </div>
                <Input placeholder="KPI" value={f.kpi} onChange={(e) => setF({ ...f, kpi: e.target.value })} />
                <Textarea placeholder="Internal notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
              </div>
              <div>
                <Label className="text-xs">Payout type (Loan campaigns ke liye % option)</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setF({ ...f, payout_type: "flat" })} data-testid="campaign-form-payout-flat"
                    className={`flex-1 h-10 rounded-lg border text-sm font-bold transition-colors ${f.payout_type !== "percent" ? "bg-[#0B223D] text-white border-[#0B223D]" : "bg-white text-slate-600 border-slate-200"}`}>Flat ₹ PO</button>
                  <button type="button" onClick={() => setF({ ...f, payout_type: "percent" })} data-testid="campaign-form-payout-percent"
                    className={`flex-1 h-10 rounded-lg border text-sm font-bold transition-colors ${f.payout_type === "percent" ? "bg-[#0B223D] text-white border-[#0B223D]" : "bg-white text-slate-600 border-slate-200"}`}>% of amount</button>
                </div>
              </div>
              {f.payout_type === "percent" ? (
                <div><Label className="text-xs">Payout % (loan/disbursed amount ka)</Label><Input type="number" step="0.1" min={0} max={100} value={f.payout_percent} onChange={(e) => setF({ ...f, payout_percent: e.target.value })} data-testid="campaign-form-payout-pct" className="font-mono-num" placeholder="e.g. 1.5" />
                <p className="text-[10px] text-slate-400 mt-1">Example: 1.5% ka matlab ₹1,00,000 disburse hone par ₹1,500 payout. Actual amount postback se credit hoga.</p></div>
              ) : (
                <div><Label className="text-xs">Payout per conversion (₹ — user/partner ko kitna milega)</Label><Input type="number" value={f.payout_pool} onChange={(e) => setF({ ...f, payout_pool: e.target.value })} data-testid="campaign-form-pool" className="font-mono-num" /></div>
              )}
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Slab payout (optional — e.g. 5+ leads par ₹350, 10+ par ₹400)</Label>
                  <button type="button" onClick={() => setF({ ...f, payout_slabs: [...(f.payout_slabs || []), { min_conversions: f.payout_slabs?.length ? Math.max(...f.payout_slabs.map((s) => Number(s.min_conversions) || 0)) + 5 : 5, payout: "" }] })} data-testid="campaign-form-slab-add" className="text-xs font-bold text-teal-700">+ Add slab</button>
                </div>
                {(f.payout_slabs || []).map((sl, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="number" min={1} value={sl.min_conversions} onChange={(e) => { const a = [...f.payout_slabs]; a[i] = { ...a[i], min_conversions: e.target.value }; setF({ ...f, payout_slabs: a }); }} data-testid={`slab-min-${i}`} className="h-9 w-20 font-mono-num text-xs" placeholder="5" />
                    <span className="text-xs text-slate-400 shrink-0">+ leads → ₹</span>
                    <Input type="number" min={0} value={sl.payout} onChange={(e) => { const a = [...f.payout_slabs]; a[i] = { ...a[i], payout: e.target.value }; setF({ ...f, payout_slabs: a }); }} data-testid={`slab-payout-${i}`} className="h-9 flex-1 font-mono-num text-xs" placeholder="400" />
                    <button type="button" onClick={() => setF({ ...f, payout_slabs: f.payout_slabs.filter((_, j) => j !== i) })} data-testid={`slab-remove-${i}`} className="text-rose-400 hover:text-rose-600 text-xs px-1">✕</button>
                  </div>
                ))}
                {(f.payout_slabs || []).length > 0 && <p className="text-[10px] text-slate-400">Base payout flat wala rahega; slab cross hone par higher rate. User ko ⓘ button par poori slab dikhegi.</p>}
              </div>
              <Textarea placeholder={"Campaign flow (one step per line)"} value={f.flow} onChange={(e) => setF({ ...f, flow: e.target.value })} rows={3} data-testid="campaign-form-flow" />
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
                <p className="text-xs font-bold text-indigo-800">Payment Terms (optional — partner settlement)</p>
                <div className="grid grid-cols-2 gap-2">
                  <select value={f.payment_frequency} onChange={(e) => setF({ ...f, payment_frequency: e.target.value })} data-testid="campaign-form-payfreq" className="h-9 rounded-lg border border-indigo-200 px-2 text-xs bg-white">
                    <option value="">— Payment frequency —</option>
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="custom">Custom</option>
                  </select>
                  <select value={f.settlement_terms} onChange={(e) => setF({ ...f, settlement_terms: e.target.value })} data-testid="campaign-form-settlement" className="h-9 rounded-lg border border-indigo-200 px-2 text-xs bg-white">
                    <option value="">— Settlement terms —</option>
                    <option value="prepaid">Prepaid</option><option value="net7">Net 7</option><option value="net15">Net 15</option><option value="net30">Net 30</option><option value="net45">Net 45</option><option value="custom">Custom</option>
                  </select>
                </div>
                <Input placeholder="Expected payment timeline (e.g. Lead approve + 7 din)" value={f.payment_timeline} onChange={(e) => setF({ ...f, payment_timeline: e.target.value })} data-testid="campaign-form-paytimeline" className="h-9 text-xs bg-white" />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min="0" placeholder="Min billable qty" value={f.min_billable_qty} onChange={(e) => setF({ ...f, min_billable_qty: e.target.value })} data-testid="campaign-form-minqty" className="h-9 text-xs bg-white font-mono-num" />
                  <Input placeholder="Holding period (e.g. 15 days)" value={f.holding_period} onChange={(e) => setF({ ...f, holding_period: e.target.value })} data-testid="campaign-form-holding" className="h-9 text-xs bg-white" />
                </div>
                <Input placeholder="Validation / approval conditions" value={f.approval_conditions} onChange={(e) => setF({ ...f, approval_conditions: e.target.value })} data-testid="campaign-form-approvalcond" className="h-9 text-xs bg-white" />
                <Textarea placeholder="Additional payment notes" value={f.payment_notes} onChange={(e) => setF({ ...f, payment_notes: e.target.value })} rows={2} data-testid="campaign-form-paynotes" className="text-xs bg-white" />
              </div>
              <Button onClick={save} className="w-full bg-[#0B223D] text-white font-bold" data-testid="campaign-form-save">{editId ? "Save changes" : "Create & announce"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Table head={["Campaign ID", "Label", "Name", "Form", "Status", "Clicks", "Leads", "PO (admin)", "Link", "Actions"]} testid="admin-campaigns-table">
        {list.filter((c) => { const s = q.trim().toLowerCase(); return !s || [c.name, c.campaign_code, c.staff_label, c.category].filter(Boolean).join(" ").toLowerCase().includes(s); }).map((c) => (
          <tr key={c.id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs font-bold text-teal-700">{c.campaign_code || "—"}</td>
            <td className="p-3.5">{c.staff_label ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-sky-100 text-sky-700" data-testid={`campaign-label-${c.id}`}>{c.staff_label}</span> : <span className="text-slate-300 text-xs">—</span>}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{c.name}</td>
            <td className="p-3.5 text-xs text-slate-500">{c.form_id ? (forms.find((x) => x.id === c.form_id)?.name || "form") : "—"}</td>
            <td className="p-3.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${c.status === "active" ? "bg-teal-50 text-teal-700" : c.status === "paused" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span></td>
            <td className="p-3.5 font-mono-num text-slate-500">{c.clicks || 0}</td>
            <td className="p-3.5 font-mono-num text-slate-500">{c.leads_count || 0}</td>
            <td className="p-3.5 font-mono-num text-slate-400">{inr(c.po_amount || 0)}</td>
            <td className="p-3.5"><div className="flex gap-1">
              <button onClick={() => copyLink(c)} data-testid={`campaign-copy-${c.id}`} title="Copy link" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Copy className="w-4 h-4" /></button>
              <button onClick={() => previewLink(c)} data-testid={`campaign-preview-${c.id}`} title="Preview" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Eye className="w-4 h-4" /></button>
            </div></td>
            <td className="p-3.5"><div className="flex gap-1">
              {c.status === "active"
                ? <button onClick={() => toggleStatus(c, "paused")} data-testid={`campaign-pause-${c.id}`} title="Pause" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Pause className="w-4 h-4" /></button>
                : <button onClick={() => toggleStatus(c, "active")} data-testid={`campaign-activate-${c.id}`} title="Activate" className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-600"><Play className="w-4 h-4" /></button>}
              <button onClick={() => openEdit(c)} data-testid={`campaign-edit-${c.id}`} title="Edit campaign" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><SlidersHorizontal className="w-4 h-4" /></button>
              <button onClick={() => emailAll(c)} data-testid={`campaign-email-${c.id}`} title="Email all users" className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600"><Mail className="w-4 h-4" /></button>
              <button onClick={() => openPins(c)} data-testid={`campaign-pincode-${c.id}`} title="Pincode targeting" className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-600"><MapPin className="w-4 h-4" /></button>
              {isAdmin && <button onClick={() => deleteCampaign(c)} data-testid={`campaign-delete-${c.id}`} title="Delete campaign" className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>}
            </div></td>
          </tr>
        ))}
      </Table>

      <Dialog open={!!pinDlg} onOpenChange={(o) => !o && setPinDlg(null)}>
        <DialogContent data-testid="pincode-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Pincode Targeting — {pinDlg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mode</Label>
              <select value={pinMode} onChange={(e) => setPinMode(e.target.value)} data-testid="pincode-mode" className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm mt-1">
                <option value="all">All India (koi restriction nahi)</option>
                <option value="allow">Allow list — sirf in pincodes par link kaam karega</option>
                <option value="block">Block list — in pincodes par link kaam nahi karega</option>
              </select>
            </div>
            <div>
              <Label>Pincode list (paste karein ya CSV/TXT upload karein — sirf 6-digit codes liye jayenge)</Label>
              <Textarea rows={6} value={pinText} onChange={(e) => setPinText(e.target.value)} data-testid="pincode-text" placeholder={"110001\n400001\n560001"} className="mt-1 font-mono-num text-xs" />
              <div className="flex items-center justify-between mt-1.5 gap-2">
                <input type="file" accept=".csv,.txt" onChange={onPinFile} data-testid="pincode-file" className="text-xs text-slate-500" />
                <span className="text-xs text-slate-400 whitespace-nowrap">Saved: {pinCount}</span>
              </div>
            </div>
            <p className="text-xs text-slate-400">Customer apply karte waqt apna pincode dega; agar list ke mutabik allowed nahi to link aage nahi badhegi.</p>
            <Button onClick={savePins} data-testid="pincode-save-btn" className="w-full bg-[#0B223D] text-white font-bold">Save pincode settings</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CollectedData() {
  const [data, setData] = useState({ count: 0, leads: [], statuses: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [flt, setFlt] = useState({ campaign_id: "", status: "", source: "", date_from: "", date_to: "", q: "", duplicate: "", adviser: "" });
  const qs = () => Object.entries(flt).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const load = () => api.get(`/admin/leads?${qs()}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const changeStatus = async (lid, status) => {
    try {
      const { data: res } = await api.post(`/admin/leads/${lid}/status`, { status });
      if (res.credit?.credited) toast.success(`Approved — ₹${res.credit.credited.toLocaleString("en-IN")} wallet me credit (${res.credit.txn_id})`);
      else if (res.credit?.note) toast.success(`Status updated — ${res.credit.note}`);
      else toast.success("Status updated");
      load();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const exportXlsx = () => downloadFile(`${BACKEND_URL}/api/admin/leads/export.xlsx?${qs()}`, "creditkendra_leads_export.xlsx");
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Collected Data</h1>
        <Button onClick={exportXlsx} data-testid="leads-export-btn" className="bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <select value={flt.campaign_id} onChange={(e) => setFlt({ ...flt, campaign_id: e.target.value })} data-testid="leads-filter-campaign" className="h-10 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All campaigns</option>{campaigns.map((c) => <option key={c.id} value={c.campaign_code || c.id}>{c.name}</option>)}</select>
        <select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} data-testid="leads-filter-status" className="h-10 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All status</option>{data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={flt.duplicate} onChange={(e) => setFlt({ ...flt, duplicate: e.target.value })} data-testid="leads-filter-duplicate" className="h-10 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All leads</option><option value="yes">⚠ Duplicates only</option><option value="no">Unique only</option></select>
        <select value={flt.adviser} onChange={(e) => setFlt({ ...flt, adviser: e.target.value })} data-testid="leads-filter-adviser" className="h-10 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All users (advisers)</option>{(data.advisers || []).map((a) => <option key={a.user_code} value={a.user_code}>{a.name ? `${a.name} (${a.user_code})` : a.user_code}</option>)}</select>
        <Input placeholder="Source" value={flt.source} onChange={(e) => setFlt({ ...flt, source: e.target.value })} className="h-10" list="lead-sources" data-testid="leads-filter-source" />
        <datalist id="lead-sources">{(data.sources || []).map((s) => <option key={s} value={s} />)}</datalist>
        <Input type="date" value={flt.date_from} onChange={(e) => setFlt({ ...flt, date_from: e.target.value })} data-testid="leads-filter-from" className="h-10" />
        <Input type="date" value={flt.date_to} onChange={(e) => setFlt({ ...flt, date_to: e.target.value })} data-testid="leads-filter-to" className="h-10" />
        <Input placeholder="Search name/mobile/ID" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} className="h-10" />
        <Button onClick={load} data-testid="leads-apply-filter" className="bg-[#0B223D] text-white h-10 lg:col-span-1 sm:col-span-3">Apply filters</Button>
      </div>
      <p className="text-sm text-slate-500">{data.count} lead(s)</p>
      <Table head={["Lead ID", "Unique ID", "Customer ID", "Campaign", "Name", "Mobile", "Email", "Pincode", "Source", "Referred By", "Status", "Created"]} testid="admin-leads-table">
        {data.leads.map((l) => (
          <tr key={l.lead_id} className={l.duplicate ? "border-t border-amber-200 bg-amber-50" : "border-t border-slate-100"}>
            <td className="p-3.5 font-mono-num text-xs font-bold text-teal-700">{l.lead_id}{l.duplicate && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-400 text-white text-[10px] font-bold" title="Duplicate lead — same mobile already applied for this campaign">DUPLICATE</span>}{l.flagged && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-rose-500 text-white text-[10px] font-bold" title={(l.fraud_flags || []).join(" | ")}>FLAGGED</span>}</td>
            <td className="p-3.5 font-mono-num text-xs font-bold text-[#0B223D]" title="Client/brand ko sirf ye Unique ID jati hai — isi se mapping karein">{l.click_uid || "-"}</td>
            <td className="p-3.5 font-mono-num text-xs text-slate-500">{l.customer_id}</td>
            <td className="p-3.5 text-slate-600">{l.campaign_name}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{l.full_name}</td>
            <td className="p-3.5 font-mono-num">{l.mobile}</td>
            <td className="p-3.5 text-slate-500 text-xs">{l.email}</td>
            <td className="p-3.5 font-mono-num">{l.pincode}</td>
            <td className="p-3.5 text-slate-500 text-xs">{l.source}</td>
            <td className="p-3.5 text-xs" data-testid={`lead-referredby-${l.lead_id}`}>{l.referred_by ? <span className="font-semibold text-[#0B223D]">{l.referred_by}</span> : <span className="text-slate-400">{l.lead_source_label || "Direct / Unknown"}</span>}</td>
            <td className="p-3.5"><select value={l.status} onChange={(e) => changeStatus(l.lead_id, e.target.value)} data-testid={`lead-status-${l.lead_id}`} className="h-9 rounded-lg border border-slate-200 px-2 text-xs capitalize">{data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
            <td className="p-3.5 text-slate-400 text-xs">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Submissions() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/submissions").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const change = async (sid, status) => {
    try { await api.post(`/admin/submissions/${sid}/status?status=${status}`); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Applications &amp; Conversions</h1>
      <p className="text-sm text-slate-500">Marking a referred application <b>converted</b> credits pending rewards; <b>approved</b> releases them to wallets.</p>
      <Table head={["Submission", "Applicant", "Campaign", "Pincode", "Status", "Action"]} testid="admin-submissions-table">
        {list.map((s) => (
          <tr key={s.id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs text-slate-500">{s.submission_id}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{s.full_name}<div className="text-xs text-slate-400">{s.mobile}</div></td>
            <td className="p-3.5 text-slate-500">{s.campaign_name}</td>
            <td className="p-3.5 font-mono-num">{s.pincode}</td>
            <td className="p-3.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">{s.status}</span></td>
            <td className="p-3.5">
              <select value={s.status} onChange={(e) => change(s.id, e.target.value)} data-testid={`sub-status-${s.submission_id}`} className="h-9 rounded-lg border border-slate-200 px-2 text-xs">
                {SUB_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function KYCQueue() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/kyc").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const review = async (uid, decision) => {
    try { await api.post(`/admin/kyc/${uid}/review?decision=${decision}`); toast.success(`KYC ${decision}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">KYC Queue</h1>
      <Table head={["User", "Name", "PAN", "Docs", "Status", "Action"]} testid="admin-kyc-table">
        {list.map((r) => (
          <tr key={r.user_id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs">{r.user_code}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{r.full_name}</td>
            <td className="p-3.5 font-mono-num">{r.pan}</td>
            <td className="p-3.5 text-xs">
              {r.pan_doc && <a href={r.pan_doc} target="_blank" rel="noreferrer" className="block text-teal-700 font-semibold underline" data-testid={`kyc-view-pan-${r.user_code}`}>PAN doc</a>}
              {r.aadhaar_doc && <a href={r.aadhaar_doc} target="_blank" rel="noreferrer" className="block text-teal-700 font-semibold underline" data-testid={`kyc-view-aadhaar-${r.user_code}`}>Aadhaar doc{r.aadhaar_not_linked ? " (manual)" : ""}</a>}
              {!r.pan_doc && !r.aadhaar_doc && <span className="text-slate-400">—</span>}
            </td>
            <td className="p-3.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">{r.status.replace(/_/g, " ")}</span></td>
            <td className="p-3.5 flex gap-2">
              <Button size="sm" onClick={() => review(r.user_id, "verified")} className="bg-teal-600 text-white h-8" data-testid={`kyc-verify-${r.user_code}`}>Verify</Button>
              <Button size="sm" variant="outline" onClick={() => review(r.user_id, "rejected")} className="h-8 text-rose-600 border-rose-200">Reject</Button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Withdrawals() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/withdrawals").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (wid, decision) => {
    try { await api.post(`/admin/withdrawals/${wid}/decision?decision=${decision}`); toast.success(`Marked ${decision}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Withdrawals</h1>
      <Table head={["User", "Amount", "Method", "Status", "Action"]} testid="admin-withdrawals-table">
        {list.map((w) => (
          <tr key={w.id} className="border-t border-slate-100">
            <td className="p-3.5">{w.user_name}<div className="text-xs font-mono-num text-slate-400">{w.user_code}</div></td>
            <td className="p-3.5 font-mono-num font-bold">{inr(w.amount)}</td>
            <td className="p-3.5 uppercase text-xs">{w.method}</td>
            <td className="p-3.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">{w.status.replace("_", " ")}</span></td>
            <td className="p-3.5 flex gap-2">
              <Button size="sm" onClick={() => decide(w.id, "paid")} className="bg-teal-600 text-white h-8" data-testid={`wd-pay-${w.id}`}>Mark Paid</Button>
              <Button size="sm" variant="outline" onClick={() => decide(w.id, "rejected")} className="h-8 text-rose-600 border-rose-200">Reject</Button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Feedback() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/feedback").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const mod = async (fid, action) => { try { await api.post(`/admin/feedback/${fid}/moderate?action=${action}`); toast.success(action); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Feedback Moderation</h1>
      <Table head={["User", "Rating", "Comment", "Status", "Action"]} testid="admin-feedback-table">
        {list.map((f) => (
          <tr key={f.id} className="border-t border-slate-100">
            <td className="p-3.5">{f.display_name}{f.verified_user && <span className="ml-1 text-[10px] text-teal-600 font-bold">✓</span>}</td>
            <td className="p-3.5 font-mono-num">{f.rating}★</td>
            <td className="p-3.5 text-slate-500 max-w-xs">{f.comment}</td>
            <td className="p-3.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">{f.status}</span></td>
            <td className="p-3.5 flex gap-2">
              <Button size="sm" onClick={() => mod(f.id, "approve")} className="bg-teal-600 text-white h-8">Approve</Button>
              <Button size="sm" variant="outline" onClick={() => mod(f.id, "feature")} className="h-8">Feature</Button>
              <Button size="sm" variant="outline" onClick={() => mod(f.id, "reject")} className="h-8 text-rose-600 border-rose-200">Reject</Button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

const ROLE_LABELS_UI = { admin: "Super Admin", manager: "Campaign Manager", staff: "Employee", user: "Adviser" };

function MyAccount() {
  const { user, refresh } = useAuth();
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingE, setSavingE] = useState(false);
  const [savingP, setSavingP] = useState(false);

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.current_password) return toast.error("Dono fields bharein");
    setSavingE(true);
    try {
      const { data } = await api.post("/auth/change-email", emailForm);
      toast.success(data.message || "Email updated");
      setEmailForm({ current_password: "", new_email: "" });
      await refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingE(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) return toast.error("Naya password kam se kam 6 characters");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Naya password aur confirm match nahi karte");
    setSavingP(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success(data.message || "Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingP(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">My Account</h1>
        <p className="text-sm text-slate-500 mt-1">Apni login ID (email) aur password yahan se badlein. Security ke liye current password confirm karna zaroori hai.</p>
      </div>

      <form onSubmit={changeEmail} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="change-email-form">
        <h2 className="font-display font-bold text-lg text-[#0B223D]">Change Login Email</h2>
        <div>
          <Label className="font-semibold text-slate-600">Current Email</Label>
          <Input value={user?.email || ""} disabled className="mt-1.5 h-11 bg-slate-50" data-testid="current-email" />
        </div>
        <div>
          <Label className="font-semibold text-slate-600">New Email</Label>
          <Input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} data-testid="new-email-input" className="mt-1.5 h-11" placeholder="naya@email.com" />
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Current Password (confirm)</Label>
          <Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="email-current-pw" className="mt-1.5 h-11" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={savingE} data-testid="change-email-btn" className="bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-11 rounded-xl">{savingE ? "Updating…" : "Update Email"}</Button>
      </form>

      <form onSubmit={changePw} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="change-password-form">
        <h2 className="font-display font-bold text-lg text-[#0B223D]">Change Password</h2>
        <div>
          <Label className="font-semibold text-slate-600">Current Password</Label>
          <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="pw-current" className="mt-1.5 h-11" placeholder="••••••••" />
        </div>
        <div>
          <Label className="font-semibold text-slate-600">New Password</Label>
          <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="pw-new" className="mt-1.5 h-11" placeholder="Min 6 characters" />
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Confirm New Password</Label>
          <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" className="mt-1.5 h-11" placeholder="Dobara type karein" />
        </div>
        <Button type="submit" disabled={savingP} data-testid="change-password-btn" className="bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-11 rounded-xl">{savingP ? "Updating…" : "Update Password"}</Button>
      </form>
    </div>
  );
}


function NetworkTree() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "user"))).catch(() => {}); }, []);
  const pick = async (u) => {
    setSelected(u); setData(null); setLoading(true);
    try { const r = await api.get(`/admin/users/${u.id}/referral-tree`); setData(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const filtered = q ? users.filter((u) => [u.user_code, u.name, u.email, u.mobile].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Referral Network Tree</h1>
        <p className="text-sm text-slate-500 mt-1">Kisi bhi adviser ko search karke unka poora multi-level downline tree + earnings dekhein.</p>
      </div>
      <div className="relative max-w-md">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Adviser search: ID / name / email / mobile…" data-testid="network-tree-search" className="bg-white" />
        {filtered.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="network-tree-results">
            {filtered.map((u) => (
              <button key={u.id} onClick={() => { pick(u); setQ(""); }} data-testid={`network-tree-pick-${u.user_code}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2">
                <span className="font-medium text-[#0B223D] text-sm">{u.name}</span>
                <span className="font-mono-num text-xs text-slate-400">{u.user_code}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div className="bg-[#0B223D] text-white rounded-2xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid="network-tree-root">
          <div>
            <p className="font-bold text-lg">{data?.root?.name || selected.name}</p>
            <p className="text-xs font-mono-num text-white/60">{selected.user_code} · Root{data?.root?.parent ? ` · Upline: ${data.root.parent.name} (${data.root.parent.user_code})` : " · No upline"}</p>
          </div>
          <Button onClick={() => downloadFile(`${BACKEND_URL}/api/admin/users/${selected.id}/referral-tree/export.xlsx`, `creditkendra_network_tree_${selected.user_code}.xlsx`)} data-testid="network-tree-export-btn" className="bg-teal-500 hover:bg-teal-400 text-[#0B223D] font-bold rounded-xl">
            Export Excel
          </Button>
        </div>
      )}
      {loading && <p className="text-sm text-slate-400">Loading tree…</p>}
      {data && !loading && <ReferralTree data={data} emptyText="Is adviser ne abhi tak kisi ko refer nahi kiya." />}
      {!selected && !loading && <p className="text-sm text-slate-400">Upar search box se koi adviser select karein.</p>}
    </div>
  );
}


function UsersAdmin() {
  const { user, impersonate } = useAuth();
  const isAdmin = user.role === "admin";
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [newPw, setNewPw] = useState("");
  const load = () => api.get("/admin/users").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openEdit = (u) => { setEditing(u); setNewPw(""); setForm({ name: u.name, email: u.email, mobile: u.mobile || "", role: u.role, active: u.active, kyc_status: u.kyc_status || "not_submitted", is_bulk: !!u.is_bulk }); };
  const impersonateUser = async (u) => {
    if (!window.confirm(`${u.name} (${u.user_code}) ke account me login karein? Aap unke account se kiye gaye actions audit log me record honge.`)) return;
    try { await impersonate(u.id); toast.success(`Ab aap ${u.name} ke account me hain`); nav("/admin"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.put(`/admin/users/${editing.id}`, form); toast.success("User updated"); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Password kam se kam 6 characters ka hona chahiye");
    if (!window.confirm(`Reset password for ${editing.user_code}? User ko naya password manually batana hoga.`)) return;
    try { await api.post(`/admin/users/${editing.id}/password`, { password: newPw }); toast.success("Password reset ho gaya"); setNewPw(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const filtered = list.filter((u) => !q || [u.user_code, u.name, u.email, u.mobile, u.role].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Users &amp; Roles</h1>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID, name, email, mobile…" data-testid="users-search-input" className="max-w-xs bg-white" />
      </div>
      <Table head={["User ID", "Name", "Email", "Mobile", "Role", "KYC", "Status", "Actions"]} testid="admin-users-table">
        {filtered.map((u) => (
          <tr key={u.id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs">{u.user_code}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{u.name}</td>
            <td className="p-3.5 text-slate-500">{u.email}</td>
            <td className="p-3.5 font-mono-num text-xs text-slate-500">{u.mobile || "—"}</td>
            <td className="p-3.5">
              <span data-testid={`user-role-${u.user_code}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${u.role === "admin" ? "bg-amber-50 text-amber-700" : u.role === "manager" ? "bg-indigo-50 text-indigo-700" : u.role === "staff" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{ROLE_LABELS_UI[u.role] || u.role}</span>
            </td>
            <td className="p-3.5 text-xs capitalize text-slate-500">{(u.kyc_status || "not_submitted").replace(/_/g, " ")}</td>
            <td className="p-3.5">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${u.active ? "bg-teal-50 text-teal-700" : "bg-rose-50 text-rose-700"}`}>{u.active ? "Active" : "Suspended"}</span>
            </td>
            <td className="p-3.5">
              {isAdmin && u.id !== user.id && (
                <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.user_code}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Edit user"><SlidersHorizontal className="w-4 h-4" /></button>
              )}
              {isAdmin && ["staff", "manager"].includes(u.role) && !user.impersonating && (
                <button onClick={() => impersonateUser(u)} data-testid={`user-loginas-${u.user_code}`} className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-600" title="Login as this user"><LogIn className="w-4 h-4" /></button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {!filtered.length && <p className="text-sm text-slate-400">Koi user nahi mila.</p>}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="user-edit-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Edit User {editing?.user_code}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="edit-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="edit-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Mobile</Label><Input data-testid="edit-user-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Role</Label>
                  <select data-testid="edit-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm">
                    {["user", "staff", "manager", "admin"].map((r) => <option key={r} value={r}>{ROLE_LABELS_UI[r]}</option>)}
                  </select>
                </div>
                <div><Label>KYC Status</Label>
                  <select data-testid="edit-user-kyc" value={form.kyc_status} onChange={(e) => setForm({ ...form, kyc_status: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm">
                    {["not_submitted", "pending_review", "verified", "rejected", "reverification_required"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div><p className="text-sm font-semibold text-[#0B223D]">Account Active</p><p className="text-xs text-slate-400">Off karne par user login nahi kar payega</p></div>
                <Switch data-testid="edit-user-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div><p className="text-sm font-semibold text-[#0B223D]">Bulk Partner</p><p className="text-xs text-slate-400">On karne par iski rates aap (admin) set karenge — user payout change nahi kar payega</p></div>
                <Switch data-testid="edit-user-bulk" checked={!!form.is_bulk} onCheckedChange={(v) => setForm({ ...form, is_bulk: v })} />
              </div>
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <p className="text-sm font-semibold text-[#0B223D] flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Reset Password</p>
                <p className="text-xs text-slate-400">Security ke liye purana password dikhta nahi. Naya password set karein aur user ko bata dein.</p>
                <div className="flex gap-2">
                  <Input data-testid="edit-user-new-password" type="text" placeholder="Naya password (min 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
                  <Button data-testid="edit-user-reset-password-btn" variant="outline" onClick={resetPw}>Reset</Button>
                </div>
              </div>
              <Button data-testid="edit-user-save-btn" onClick={saveEdit} className="w-full">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Audit() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Audit Logs</h1>
      <Table head={["Actor", "Role", "Action", "Entity", "When"]} testid="admin-audit-table">
        {list.map((a) => (
          <tr key={a.id} className="border-t border-slate-100">
            <td className="p-3.5 font-medium text-[#0B223D]">{a.actor_name}</td>
            <td className="p-3.5 capitalize text-slate-500">{a.role}</td>
            <td className="p-3.5">{a.action}</td>
            <td className="p-3.5 text-slate-500">{a.entity}</td>
            <td className="p-3.5 text-xs text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

const FIELD_TYPES = ["text", "name", "mobile", "email", "dob", "gender", "pan", "pincode", "city", "state", "address", "number", "currency", "dropdown", "radio", "checkbox", "consent", "hidden"];
const QUICK_FIELDS = [
  ["name", "Full Name", true], ["mobile", "Mobile Number", true], ["email", "Email", true],
  ["pan", "PAN Number", false], ["pincode", "Pincode", false], ["dob", "Date of Birth", false],
  ["address", "Address", false], ["city", "City", false], ["state", "State", false],
  ["consent", "Consent Checkbox", true],
];

function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [sel, setSel] = useState(null);
  const [fields, setFields] = useState([]);
  const [newName, setNewName] = useState("");
  const [ft, setFt] = useState("text");
  const [flabel, setFlabel] = useState("");
  const [freq, setFreq] = useState(true);
  const [fopts, setFopts] = useState("");
  const load = () => api.get("/admin/forms").then((r) => setForms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const open = async (id) => {
    const { data } = await api.get(`/admin/forms/${id}`);
    setSel(data);
    const last = data.versions?.[data.versions.length - 1];
    setFields(last ? last.fields : []);
  };
  const createForm = async () => {
    if (!newName) return;
    await api.post("/admin/forms", { name: newName });
    setNewName(""); toast.success("Form created"); load();
  };
  const addField = () => {
    if (!flabel) return;
    const key = flabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const f = { type: ft, label: flabel, key, required: freq };
    if (["dropdown", "radio", "checkbox"].includes(ft)) f.options = fopts.split(",").map((s) => s.trim()).filter(Boolean);
    setFields([...fields, f]); setFlabel(""); setFopts("");
  };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= fields.length) return; const a = [...fields];[a[i], a[j]] = [a[j], a[i]]; setFields(a); };
  const remove = (i) => setFields(fields.filter((_, j) => j !== i));
  const toggleQuick = (type, label, req) => {
    const idx = fields.findIndex((f) => f.type === type && f.label === label);
    if (idx >= 0) { remove(idx); return; }
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    setFields([...fields, { type, label, key, required: req }]);
  };
  const quickAdded = (type, label) => fields.some((f) => f.type === type && f.label === label);
  const saveVersion = async (publish) => {
    await api.post(`/admin/forms/${sel.id}/version`, { fields, publish });
    toast.success(publish ? "Published new version" : "Draft version saved"); open(sel.id); load();
  };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Dynamic Form Builder</h1>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-display font-bold text-[#0B223D] mb-3">Forms</h3>
          <div className="flex gap-2 mb-3">
            <Input placeholder="New form name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="form-new-name" className="h-10" />
            <Button onClick={createForm} className="bg-[#0B223D] text-white h-10" data-testid="form-create-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {forms.map((f) => (
              <button key={f.id} onClick={() => open(f.id)} data-testid={`form-item-${f.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-semibold ${sel?.id === f.id ? "bg-[#0B223D] text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>
                {f.name}
                <span className="block text-xs font-normal opacity-70">{f.versions?.length || 0} versions · {f.published_version ? `v${f.published_version} live` : "draft"}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
          {!sel ? <p className="text-slate-400 text-sm">Select or create a form to start building.</p> : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-[#0B223D]">{sel.name}</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveVersion(false)} data-testid="form-save-draft">Save draft</Button>
                  <Button size="sm" onClick={() => saveVersion(true)} className="bg-teal-600 text-white" data-testid="form-publish">Publish new version</Button>
                </div>
              </div>
              <div className="space-y-2 mb-4" data-testid="form-fields-list">
                {fields.length === 0 && <p className="text-slate-400 text-sm">No fields yet. Add fields below.</p>}
                {fields.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                    <span className="text-xs font-bold uppercase text-emerald-brand w-20">{f.type}</span>
                    <div className="flex-1 min-w-0"><p className="font-semibold text-[#0B223D] text-sm">{f.label} {f.required && <span className="text-rose-500">*</span>}</p><p className="text-xs font-mono-num text-slate-400">{f.key}{f.options ? ` · ${f.options.join(", ")}` : ""}</p></div>
                    <button onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-[#0B223D]"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-[#0B223D]"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => remove(i)} className="p-1 text-rose-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">Quick add / remove — requirement ke hisaab se tap karo:</p>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="form-quick-fields">
                  {QUICK_FIELDS.map(([type, label, req]) => (
                    <button key={label} type="button" onClick={() => toggleQuick(type, label, req)} data-testid={`quick-field-${type}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${quickAdded(type, label) ? "bg-teal-600 border-teal-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-teal-400"}`}>
                      {quickAdded(type, label) ? "✓ " : "+ "}{label}{req ? " *" : ""}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <select value={ft} onChange={(e) => setFt(e.target.value)} data-testid="form-field-type" className="h-10 rounded-lg border border-slate-200 px-3 text-sm">{FIELD_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                  <Input placeholder="Field label" value={flabel} onChange={(e) => setFlabel(e.target.value)} data-testid="form-field-label" className="h-10" />
                  {["dropdown", "radio", "checkbox"].includes(ft) && <Input placeholder="Options (comma separated)" value={fopts} onChange={(e) => setFopts(e.target.value)} className="h-10 sm:col-span-2" />}
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={freq} onChange={(e) => setFreq(e.target.checked)} /> Required</label>
                  <Button onClick={addField} className="bg-[#0B223D] text-white h-10" data-testid="form-add-field"><Plus className="w-4 h-4 mr-1" /> Add field</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Popups() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { title: "", description: "", benefit: "", cta_text: "Claim Offer", cta_url: "/products", desktop_creative: "", priority: 1, active: true, audience: "all", page: "home", trigger: "delay", delay_seconds: 6 };
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/popups").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openEdit = (p) => {
    setEditId(p.id);
    setF({ ...blank, title: p.title, description: p.description, benefit: p.benefit || "", cta_text: p.cta_text || "Claim Offer", cta_url: p.cta_url || "/products", desktop_creative: p.desktop_creative || "", priority: p.priority ?? 1, active: !!p.active, audience: p.audience || "all", page: p.page || "home", trigger: p.trigger || "delay", delay_seconds: p.delay_seconds ?? 6 });
    setOpen(true);
  };
  const save = async () => {
    const payload = { ...f, priority: Number(f.priority), delay_seconds: Number(f.delay_seconds) };
    try {
      if (editId) { await api.put(`/admin/popups/${editId}`, payload); toast.success("Popup updated"); }
      else { await api.post("/admin/popups", payload); toast.success("Popup created"); }
      setOpen(false); setEditId(null); setF(blank); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (p) => { await api.put(`/admin/popups/${p.id}`, { ...p, active: !p.active }); toast.success(p.active ? "Popup band kar diya" : "Popup on kar diya"); load(); };
  const del = async (id) => { await api.delete(`/admin/popups/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Exclusive Offer Popups</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setF(blank); } }}>
          <DialogTrigger asChild><Button className="bg-[#0B223D] text-white font-bold rounded-xl" data-testid="admin-add-popup-btn"><Plus className="w-4 h-4 mr-1.5" /> New popup</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>{editId ? "Edit Popup" : "New Popup"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="popup-form-title" />
              <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
              <Input placeholder="Benefit / payout (e.g. +20% commission)" value={f.benefit} onChange={(e) => setF({ ...f, benefit: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="CTA text" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} />
                <Input placeholder="CTA url" value={f.cta_url} onChange={(e) => setF({ ...f, cta_url: e.target.value })} />
              </div>
              <Input placeholder="Creative image URL (optional)" value={f.desktop_creative} onChange={(e) => setF({ ...f, desktop_creative: e.target.value })} />
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Priority</Label><Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} /></div>
                <div><Label className="text-xs">Delay (s)</Label><Input type="number" value={f.delay_seconds} onChange={(e) => setF({ ...f, delay_seconds: e.target.value })} /></div>
                <div><Label className="text-xs">Audience</Label><select value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-2"><option value="all">All</option><option value="guest">Guests</option><option value="user">Logged-in</option></select></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="popup-form-active" /> Active (off = popup band)</label>
              <Button onClick={save} className="w-full bg-[#0B223D] text-white font-bold" data-testid="popup-form-save">{editId ? "Save changes" : "Create popup"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Table head={["Title", "Priority", "Audience", "Impressions", "CTA", "CTR", "Active", ""]} testid="admin-popups-table">
        {list.map((p) => {
          const ctr = p.impressions ? ((p.cta_clicks / p.impressions) * 100).toFixed(1) : "0";
          return (
            <tr key={p.id} className="border-t border-slate-100">
              <td className="p-3.5 font-medium text-[#0B223D]">{p.title}</td>
              <td className="p-3.5 font-mono-num">{p.priority}</td>
              <td className="p-3.5 capitalize text-slate-500">{p.audience}</td>
              <td className="p-3.5 font-mono-num">{p.impressions || 0}</td>
              <td className="p-3.5 font-mono-num">{p.cta_clicks || 0}</td>
              <td className="p-3.5 font-mono-num font-bold text-emerald-brand">{ctr}%</td>
              <td className="p-3.5"><Switch checked={!!p.active} onCheckedChange={() => toggle(p)} data-testid={`popup-active-${p.id}`} /></td>
              <td className="p-3.5"><div className="flex gap-1">
                <button onClick={() => openEdit(p)} data-testid={`popup-edit-${p.id}`} title="Edit" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><SlidersHorizontal className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} data-testid={`popup-delete-${p.id}`} className="text-rose-400 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div></td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}

function Reports() {
  const base = `${BACKEND_URL}/api/admin/reports`;
  const [range, setRange] = useState({ from: "", to: "" });
  const dl = (kind, fmt) => {
    let qs = "";
    const p = [];
    if (["finance", "traffic"].includes(kind)) {
      if (range.from) p.push(`date_from=${range.from}`);
      if (range.to) p.push(`date_to=${range.to}`);
    }
    if (kind === "traffic" && seg) p.push(`segment=${encodeURIComponent(seg)}`);
    if (p.length) qs = "?" + p.join("&");
    downloadFile(`${base}/${kind}.${fmt}${qs}`, `creditkendra_${kind}_report.${fmt}`);
  };
  const [seg, setSeg] = useState("");
  const cards = [
    ["finance", "Finance Ledger", "All wallet ledger transactions — rewards, withdrawals, status.", Wallet],
    ["campaigns", "Campaign Performance", "Applications, conversions and rewards paid per campaign.", BarChart3],
    ["traffic", "Segment Traffic", "Kis segment/campaign me kitna traffic — source, IP & device details ke saath.", Target],
  ];
  const fmts = [["csv", "CSV", FileText], ["xlsx", "Excel", FileSpreadsheet], ["pdf", "PDF", FileText]];
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Reports &amp; Export</h1>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs font-semibold text-slate-500">Finance ledger — date from</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="report-date-from" className="h-10 mt-1" /></div>
        <div><Label className="text-xs font-semibold text-slate-500">Date to</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="report-date-to" className="h-10 mt-1" /></div>
        <div><Label className="text-xs font-semibold text-slate-500">Segment (traffic report)</Label><select value={seg} onChange={(e) => setSeg(e.target.value)} data-testid="report-segment" className="h-10 mt-1 rounded-lg border border-slate-200 px-2 text-sm bg-white"><option value="">— All segments —</option>{SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        <p className="text-xs text-slate-400 pb-2">Date range Finance Ledger + Segment Traffic par lagti hai. Blank = all time.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {cards.map(([kind, title, desc, Icon]) => (
          <div key={kind} className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
            <h3 className="font-display font-bold text-lg text-[#0B223D]">{title}</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">{desc}</p>
            <div className="flex gap-2">
              {fmts.map(([fmt, label, FI]) => (
                <Button key={fmt} variant="outline" onClick={() => dl(kind, fmt)} data-testid={`report-${kind}-${fmt}`} className="font-semibold">
                  <Download className="w-4 h-4 mr-1.5" /> {label}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessMatrix() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "user"))).catch(() => {}); }, []);
  const loadRows = (id) => { setUid(id); if (id) api.get(`/admin/users/${id}/campaign-access`).then((r) => setRows(r.data)).catch(() => {}); };
  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const saveRow = async (r) => {
    try {
      await api.post(`/admin/users/${uid}/campaign-access`, { campaign_id: r.campaign_id, visible: r.visible, max_payout_override: r.max_payout_override === "" || r.max_payout_override == null ? null : Number(r.max_payout_override) });
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Campaign Access Matrix</h1>
      <p className="text-sm text-slate-500">Control which campaigns each user sees and cap their max payout (give top performers higher rates, hide or lower others).</p>
      <select value={uid} onChange={(e) => loadRows(e.target.value)} data-testid="access-user-select" className="w-full max-w-md h-11 rounded-xl border border-slate-200 px-3 bg-white font-medium">
        <option value="">— Select a user —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.user_code} · {u.name}</option>)}
      </select>
      {uid && (
        <Table head={["Campaign", "Pool", "Visible", "Max payout override (₹)", ""]} testid="access-matrix-table">
          {rows.map((r, i) => (
            <tr key={r.campaign_id} className="border-t border-slate-100">
              <td className="p-3.5 font-medium text-[#0B223D]">{r.name}</td>
              <td className="p-3.5 font-mono-num text-slate-500">{inr(r.payout_pool)}</td>
              <td className="p-3.5"><Switch checked={!!r.visible} onCheckedChange={(v) => update(i, { visible: v })} data-testid={`access-visible-${r.campaign_id}`} /></td>
              <td className="p-3.5"><Input type="number" value={r.max_payout_override ?? ""} placeholder={`max ${r.max_payout || r.payout_pool}`} onChange={(e) => update(i, { max_payout_override: e.target.value })} className="h-9 w-36 font-mono-num" data-testid={`access-override-${r.campaign_id}`} /></td>
              <td className="p-3.5"><Button size="sm" onClick={() => saveRow(r)} className="bg-[#0B223D] text-white h-8" data-testid={`access-save-${r.campaign_id}`}>Save</Button></td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

const WEEK = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

function Funnel() {
  const [data, setData] = useState({ rows: [], totals: { clicks: 0, leads: 0, submitted: 0, conversions: 0 } });
  useEffect(() => { api.get("/admin/funnel").then((r) => setData(r.data)).catch(() => {}); }, []);
  const t = data.totals;
  const stages = [
    { label: "Clicks", val: t.clicks, color: "bg-sky-500" },
    { label: "Leads", val: t.leads, color: "bg-indigo-500" },
    { label: "Submitted", val: t.submitted, color: "bg-amber-500" },
    { label: "Conversions", val: t.conversions, color: "bg-teal-600" },
  ];
  const maxV = Math.max(t.clicks, 1);
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Campaign Funnel</h1>
      <p className="text-sm text-slate-500">Clicks → Leads → Submitted → Conversions. Conversions advertiser postback se confirm hoti hain.</p>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3" data-testid="funnel-overview">
        {stages.map((s) => (
          <div key={s.label} className="flex items-center gap-3">
            <div className="w-28 text-sm font-semibold text-slate-600 shrink-0">{s.label}</div>
            <div className="flex-1 bg-slate-100 rounded-lg h-8 overflow-hidden">
              <div className={`h-full ${s.color} rounded-lg transition-all flex items-center justify-end pr-2`} style={{ width: `${Math.max(s.val / maxV * 100, s.val ? 6 : 0)}%` }}>
                <span className="text-white text-xs font-bold font-mono-num">{s.val}</span>
              </div>
            </div>
          </div>
        ))}
        <div className="flex gap-6 pt-2 text-sm">
          <span className="text-slate-500">Click→Lead: <b className="text-indigo-700">{t.clicks ? (t.leads / t.clicks * 100).toFixed(1) : 0}%</b></span>
          <span className="text-slate-500">Lead→Conversion: <b className="text-teal-700">{t.leads ? (t.conversions / t.leads * 100).toFixed(1) : 0}%</b></span>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="p-3.5">Campaign</th><th className="p-3.5">Clicks</th><th className="p-3.5">Leads</th><th className="p-3.5">Submitted</th><th className="p-3.5">Conversions</th><th className="p-3.5">Lead %</th><th className="p-3.5">Conv %</th>
          </tr></thead>
          <tbody data-testid="funnel-table">
            {data.rows.map((r) => (
              <tr key={r.campaign_id} className="border-t border-slate-100">
                <td className="p-3.5"><span className="font-semibold text-[#0B223D]">{r.name}</span><br /><span className="text-xs text-slate-400 font-mono-num">{r.campaign_code || "-"}</span></td>
                <td className="p-3.5 font-mono-num">{r.clicks}</td>
                <td className="p-3.5 font-mono-num">{r.leads}</td>
                <td className="p-3.5 font-mono-num">{r.submitted}</td>
                <td className="p-3.5 font-mono-num font-bold text-teal-700">{r.conversions}</td>
                <td className="p-3.5 font-mono-num text-indigo-600">{r.lead_rate}%</td>
                <td className="p-3.5 font-mono-num text-teal-600">{r.conv_rate}%</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-sm">Abhi koi traffic/lead data nahi hai.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KycReminders() {
  const [data, setData] = useState({ count: 0, gentle: 0, final: 0, logs: [] });
  useEffect(() => { api.get("/admin/kyc-reminder-logs").then((r) => setData(r.data)).catch(() => {}); }, []);
  const stageBadge = (s) => s === "final"
    ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700">Final (72h)</span>
    : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">Gentle (24h)</span>;
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">KYC Reminder Logs</h1>
      <p className="text-sm text-slate-500">Auto KYC reminder emails — signup ke 24h (gentle) aur 72h (final) baad KYC-pending advisers ko jaate hain.</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-xs text-slate-400">Total Sent</p><p className="font-display text-2xl font-extrabold text-[#0B223D]">{data.count}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-xs text-slate-400">Gentle (24h)</p><p className="font-display text-2xl font-extrabold text-amber-600">{data.gentle}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-xs text-slate-400">Final (72h)</p><p className="font-display text-2xl font-extrabold text-rose-600">{data.final}</p></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="p-3.5">Sent At</th><th className="p-3.5">Adviser</th><th className="p-3.5">Email</th><th className="p-3.5">Stage</th><th className="p-3.5">KYC Status</th>
          </tr></thead>
          <tbody data-testid="kyc-reminder-table">
            {data.logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="p-3.5 text-xs text-slate-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-3.5 text-xs"><span className="font-semibold text-[#0B223D]">{l.user_name}</span><br /><span className="text-slate-400 font-mono-num">{l.user_code}</span></td>
                <td className="p-3.5 text-xs text-slate-500">{l.email}</td>
                <td className="p-3.5">{stageBadge(l.stage)}</td>
                <td className="p-3.5 text-xs capitalize text-slate-500">{(l.kyc_status || "-").replace("_", " ")}</td>
              </tr>
            ))}
            {data.logs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400 text-sm">Abhi koi KYC reminder nahi gaya.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostbackLogs() {
  const [data, setData] = useState({ count: 0, total_payout: 0, logs: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/postback/test", {});
      setTestRes(data);
      toast[data.ok ? "success" : "error"](data.ok ? "Postback chain OK ✓" : "Kuch step fail hua — detail dekhein");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const load = () => {
    const p = [];
    if (q.trim()) p.push(`q=${encodeURIComponent(q.trim())}`);
    if (status) p.push(`status=${status}`);
    api.get(`/admin/postback-logs${p.length ? `?${p.join("&")}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status]);
  const badge = (s) => {
    const m = { approved: "bg-teal-50 text-teal-700", disbursed: "bg-indigo-50 text-indigo-700", rejected: "bg-rose-50 text-rose-700", processing: "bg-amber-50 text-amber-700" };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${m[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Postback / Conversion Logs</h1>
        <div className="flex gap-2">
          <Button onClick={runTest} disabled={testing} data-testid="postback-test-btn" variant="outline" className="font-bold rounded-xl border-indigo-200 text-indigo-700"><Repeat className="w-4 h-4 mr-1.5" /> {testing ? "Testing…" : "Test Postback"}</Button>
          <Button onClick={() => downloadFile(`${BACKEND_URL}/api/admin/postback-logs/export.xlsx`, "creditkendra_postback_logs.xlsx")} data-testid="postback-export-btn" className="bg-[#0B223D] text-white font-bold rounded-xl"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
        </div>
      </div>
      {testRes && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="postback-test-result">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-[#0B223D]">Test Result — {testRes.ok ? <span className="text-teal-600">All steps passed ✓</span> : <span className="text-rose-600">Failed ✕</span>}</h3>
            <button onClick={() => setTestRes(null)} className="text-slate-400 hover:text-slate-600 text-sm">✕ close</button>
          </div>
          <div className="space-y-1.5">
            {testRes.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className={s.ok ? "text-teal-600" : "text-rose-600"}>{s.ok ? "✓" : "✕"}</span>
                <span className="font-semibold text-slate-700">{s.step}:</span>
                <span className="text-slate-500 break-all">{s.detail}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">{testRes.note}</p>
        </div>
      )}
      <p className="text-sm text-slate-500">Advertiser (Offer18/click2track) se aane wali har conversion yahan log hoti hai. Client UID se adviser + payout turant map karein.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-xs text-slate-400">Total Conversions</p><p className="font-display text-2xl font-extrabold text-[#0B223D]">{data.count}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-xs text-slate-400">Total Payout Credited</p><p className="font-display text-2xl font-extrabold text-teal-700">{inr(data.total_payout)}</p></div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Client UID / Tracking UID / Lead ID / Adviser / Txn" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="postback-search" className="h-10 max-w-xs" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="postback-status-filter" className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white"><option value="">All status</option><option value="approved">Approved</option><option value="disbursed">Disbursed</option><option value="rejected">Rejected</option><option value="processing">Processing</option></select>
        <Button onClick={load} variant="outline" className="h-10" data-testid="postback-search-btn">Search</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="p-3.5">Date</th><th className="p-3.5">Client UID</th><th className="p-3.5">Lead ID</th><th className="p-3.5">Campaign</th><th className="p-3.5">Adviser</th><th className="p-3.5">Status</th><th className="p-3.5">Payout</th><th className="p-3.5">Txn</th>
          </tr></thead>
          <tbody data-testid="postback-logs-table">
            {data.logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="p-3.5 text-xs text-slate-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-3.5 font-mono-num text-xs font-bold text-indigo-700">{l.click_uid || "-"}</td>
                <td className="p-3.5 font-mono-num text-xs text-teal-700">{l.lead_id}</td>
                <td className="p-3.5 text-xs">{l.campaign_code || l.campaign || "-"}</td>
                <td className="p-3.5 text-xs"><span className="font-semibold text-[#0B223D]">{l.adviser_name || "-"}</span><br /><span className="text-slate-400 font-mono-num">{l.adviser_code || "-"}</span></td>
                <td className="p-3.5">{badge(l.status)}</td>
                <td className="p-3.5 font-mono-num font-bold text-teal-700">{l.payout ? inr(l.payout) : "-"}</td>
                <td className="p-3.5 text-xs text-slate-400">{l.txn || "-"}</td>
              </tr>
            ))}
            {data.logs.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-sm">Abhi koi postback conversion nahi aayi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Settings() {
  const [s, setS] = useState({ cibil_link: "", cibil_title: "", cibil_desc: "", cibil_btn_label: "", min_withdrawal: 300, withdrawals_enabled: true, withdrawal_days: [], support_phone: "", support_whatsapp: "", support_email: "", support_address: "", signup_bonus: 100, signup_bonus_withdraw_percent: 5, referral_bonus_amount: 100, referral_monthly_cap: 10, referral_bonus_min_withdrawal: 1000, sip_cta_label: "", sip_cta_url: "", emi_cta_label: "", emi_cta_url: "", maintenance_mode: false, google_site_verification: "", google_analytics_id: "", logo_url: "" });
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const m = Object.fromEntries((r.data || []).map((x) => [x.id, x.value]));
      setS((p) => ({ ...p, ...m, withdrawal_days: m.withdrawal_days || [] }));
    }).catch(() => {});
  }, []);
  const toggleDay = (d) => setS({ ...s, withdrawal_days: s.withdrawal_days.includes(d) ? s.withdrawal_days.filter((x) => x !== d) : [...s.withdrawal_days, d] });
  const save = async () => {
    try { await api.post("/admin/settings", { ...s, min_withdrawal: Number(s.min_withdrawal), signup_bonus: Number(s.signup_bonus), signup_bonus_withdraw_percent: Number(s.signup_bonus_withdraw_percent), referral_bonus_amount: Number(s.referral_bonus_amount), referral_monthly_cap: Number(s.referral_monthly_cap), referral_bonus_min_withdrawal: Number(s.referral_bonus_min_withdrawal) }); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const field = (k, label, props = {}) => (
    <div><Label className="font-semibold text-slate-600">{label}</Label><Input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} className="mt-1.5 h-11" {...props} /></div>
  );
  const [segs, setSegs] = useState({ segments: [], custom: [] });
  const [newSeg, setNewSeg] = useState("");
  const loadSegs = () => api.get("/segments").then((r) => setSegs(r.data)).catch(() => {});
  useEffect(() => { loadSegs(); }, []);
  const addSeg = async () => {
    if (!newSeg.trim()) return;
    try { await api.post("/admin/segments", { name: newSeg.trim() }); setNewSeg(""); loadSegs(); toast.success("Segment add ho gaya"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const renameSeg = async (old) => {
    const nw = window.prompt(`"${old}" ka naya naam:`, old);
    if (!nw || !nw.trim() || nw.trim() === old) return;
    try { await api.put("/admin/segments/rename", { old, new: nw.trim() }); loadSegs(); toast.success("Segment rename ho gaya (campaigns bhi update ho gaye)"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delSeg = async (name) => {
    if (!window.confirm(`Segment "${name}" delete karein? Campaigns par set value waisi hi rahegi.`)) return;
    try { await api.delete(`/admin/segments/${encodeURIComponent(name)}`); loadSegs(); toast.success("Segment delete ho gaya"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Platform Settings</h1>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <h3 className="font-display font-bold text-[#0B223D]">Withdrawals</h3>
        <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
          <div><p className="font-semibold text-[#0B223D]">Withdrawals enabled</p><p className="text-sm text-slate-400">Turn off to disable all withdrawal requests.</p></div>
          <Switch checked={!!s.withdrawals_enabled} onCheckedChange={(v) => setS({ ...s, withdrawals_enabled: v })} data-testid="settings-withdrawals-enabled" />
        </div>
        <div><Label className="font-semibold text-slate-600">Minimum withdrawal (₹)</Label><Input type="number" value={s.min_withdrawal} onChange={(e) => setS({ ...s, min_withdrawal: e.target.value })} data-testid="settings-min-withdrawal" className="mt-1.5 h-11 font-mono-num" /></div>
        <div>
          <Label className="font-semibold text-slate-600">Allowed withdrawal days</Label>
          <p className="text-xs text-slate-400 mb-2">Leave all unchecked to allow every day.</p>
          <div className="flex flex-wrap gap-2">
            {WEEK.map(([name, d]) => (
              <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`settings-day-${d}`}
                className={`px-3 py-2 rounded-lg text-sm font-semibold ${s.withdrawal_days.includes(d) ? "bg-[#0B223D] text-white" : "bg-slate-100 text-slate-600"}`}>{name}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <h3 className="font-display font-bold text-[#0B223D]">Signup Bonus</h3>
        <div><Label className="font-semibold text-slate-600">Signup bonus points (₹)</Label><Input type="number" value={s.signup_bonus} onChange={(e) => setS({ ...s, signup_bonus: e.target.value })} data-testid="settings-signup-bonus" className="mt-1.5 h-11 font-mono-num" /></div>
        <div><Label className="font-semibold text-slate-600">Bonus % added per withdrawal</Label><Input type="number" value={s.signup_bonus_withdraw_percent} onChange={(e) => setS({ ...s, signup_bonus_withdraw_percent: e.target.value })} data-testid="settings-bonus-percent" className="mt-1.5 h-11 font-mono-num" /></div>
        <p className="text-xs text-slate-400">Naye users ko registration par bonus points milte hain. Har withdrawal par bonus ka yeh % automatically add hota hai.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <h3 className="font-display font-bold text-[#0B223D]">Referral Bonus (Sign-up refer)</h3>
        <div><Label className="font-semibold text-slate-600">Referral bonus amount (₹ per referral)</Label><Input type="number" value={s.referral_bonus_amount} onChange={(e) => setS({ ...s, referral_bonus_amount: e.target.value })} data-testid="settings-referral-bonus-amount" className="mt-1.5 h-11 font-mono-num" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="font-semibold text-slate-600">Per month eligible refers</Label><Input type="number" value={s.referral_monthly_cap} onChange={(e) => setS({ ...s, referral_monthly_cap: e.target.value })} data-testid="settings-referral-monthly-cap" className="mt-1.5 h-11 font-mono-num" /></div>
          <div><Label className="font-semibold text-slate-600">Min withdrawal to unlock (₹)</Label><Input type="number" value={s.referral_bonus_min_withdrawal} onChange={(e) => setS({ ...s, referral_bonus_min_withdrawal: e.target.value })} data-testid="settings-referral-min-withdrawal" className="mt-1.5 h-11 font-mono-num" /></div>
        </div>
        <p className="text-xs text-slate-400">Referrer ko bonus tabhi credit hoga jab referred user earn karke pehla qualifying withdrawal karega. Per month sirf itne referrals eligible honge.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-[#0B223D]">Credit Score Card (Tools page)</h3>
        <div><Label className="font-semibold text-slate-600">Card title</Label><Input value={s.cibil_title} onChange={(e) => setS({ ...s, cibil_title: e.target.value })} data-testid="settings-cibil-title" className="mt-1.5 h-11" placeholder="Free Credit Score Check" /></div>
        <div><Label className="font-semibold text-slate-600">Description</Label><Textarea rows={2} value={s.cibil_desc} onChange={(e) => setS({ ...s, cibil_desc: e.target.value })} data-testid="settings-cibil-desc" className="mt-1.5" placeholder="Check your credit score instantly…" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="font-semibold text-slate-600">Button label</Label><Input value={s.cibil_btn_label} onChange={(e) => setS({ ...s, cibil_btn_label: e.target.value })} data-testid="settings-cibil-btn" className="mt-1.5 h-11" placeholder="Check my score" /></div>
          <div><Label className="font-semibold text-slate-600">Button link (koi bhi URL — brand name zaroori nahi)</Label><Input value={s.cibil_link} onChange={(e) => setS({ ...s, cibil_link: e.target.value })} data-testid="settings-cibil-link" className="mt-1.5 h-11" placeholder="https://…" /></div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-[#0B223D]">Contact details &amp; CIBIL link</h3>
        {field("support_phone", "Support phone")}
        {field("support_whatsapp", "WhatsApp number (with country code, e.g. 9172...)")}
        {field("support_email", "Support email")}
        {field("support_address", "Office address")}
        {field("cibil_link", "CIBIL Check partner link", { placeholder: "https://...  (blank = coming soon)" })}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-[#0B223D]">Calculator Brand CTAs</h3>
        <p className="text-xs text-slate-400">Calculators ke result ke neeche ek brand button dikhega. Blank chhodo to button nahi dikhega.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("sip_cta_label", "SIP button text", { placeholder: "e.g. Start SIP with Groww" })}
          {field("sip_cta_url", "SIP button link", { placeholder: "https://..." })}
          {field("emi_cta_label", "EMI button text", { placeholder: "e.g. Apply Loan with Axis" })}
          {field("emi_cta_url", "EMI button link", { placeholder: "https://..." })}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="settings-segments-card">
        <h3 className="font-display font-bold text-[#0B223D]">Manage Segments</h3>
        <p className="text-xs text-slate-400">Campaign segments yahan manage karein. Default segments (grey) edit/delete nahi ho sakte; aapke custom segments (green) rename/delete ho sakte hain — rename karne par campaigns bhi auto-update honge.</p>
        <div className="flex gap-2">
          <Input value={newSeg} onChange={(e) => setNewSeg(e.target.value)} data-testid="segment-new-input" placeholder="Naya segment naam (e.g. Gold Loan)" className="h-10" />
          <Button type="button" onClick={addSeg} data-testid="segment-add-btn" className="bg-[#0B223D] text-white h-10 shrink-0"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(segs.segments || []).map((s) => {
            const isCustom = (segs.custom || []).includes(s);
            return (
              <span key={s} data-testid={`segment-item-${s}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border ${isCustom ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                {s}
                {isCustom && <button type="button" onClick={() => renameSeg(s)} data-testid={`segment-rename-${s}`} className="text-indigo-500 hover:text-indigo-700 font-bold" title="Rename">✎</button>}
                {isCustom && <button type="button" onClick={() => delSeg(s)} data-testid={`segment-delete-${s}`} className="text-rose-500 hover:text-rose-700 font-bold" title="Delete">✕</button>}
              </span>
            );
          })}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="settings-logo-card">
        <h3 className="font-display font-bold text-[#0B223D]">Brand Logo</h3>
        <p className="text-xs text-slate-500 leading-relaxed">
          Apna logo upload karein. <b className="text-[#0B223D]">Recommended size: 240 × 64 px</b> (width × height) — PNG with transparent background, 1 MB se kam.
          Logo website navbar me <b>~40px height</b> par aur footer me <b>~42px height</b> par render hota hai. Isliye <b>horizontal (chaudai zyada, height kam)</b> logo banayein taaki mobile aur desktop dono par proper dikhe (square ya tall logo chhota dikhega).
        </p>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <p className="text-[11px] text-slate-400 mb-1 font-semibold">Website navbar preview (40px)</p>
            <div className="bg-white border border-slate-200 rounded-xl px-4 flex items-center" style={{ height: 64 }}>
              <img src={s.logo_url || "/logo-mark.png"} alt="logo preview" style={{ height: 40 }} className="w-auto object-contain" data-testid="logo-preview-light" />
            </div>
          </div>
          <div>
            <p className="text-[11px] text-slate-400 mb-1 font-semibold">Footer / dark preview (42px)</p>
            <div className="bg-[#0B223D] border border-slate-200 rounded-xl px-4 flex items-center" style={{ height: 64 }}>
              <img src={s.logo_url || "/logo-mark.png"} alt="logo preview dark" style={{ height: 42 }} className="w-auto object-contain" data-testid="logo-preview-dark" />
            </div>
          </div>
        </div>
        <div>
          <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" data-testid="settings-logo-upload"
            onChange={(e) => { const f = e.target.files[0]; if (!f) return; if (f.size > 1024 * 1024) { toast.error("Logo 1 MB se kam hona chahiye"); return; } const r = new FileReader(); r.onload = () => setS((p) => ({ ...p, logo_url: r.result })); r.readAsDataURL(f); }}
            className="block w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[#0B223D] file:text-white file:font-semibold" />
          {s.logo_url && <button type="button" onClick={() => setS((p) => ({ ...p, logo_url: "" }))} data-testid="settings-logo-reset" className="mt-2 text-sm text-rose-600 font-semibold">Reset to default logo</button>}
          <p className="text-[11px] text-slate-400 mt-1">Upload karke neeche "Save all settings" dabayein. Preview turant update hota hai.</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="settings-seo-card">
        <h3 className="font-display font-bold text-[#0B223D]">Google Search Console &amp; Google Analytics</h3>
        <p className="text-xs text-slate-400">Google par website jaldi index karane aur traffic track karne ke liye yahan codes daalein (optional). Jab bhi ready ho paste karke Save karein.</p>
        <div>
          <Label className="font-semibold text-slate-600">Google Search Console Verification Code</Label>
          <Input value={s.google_site_verification || ""} onChange={(e) => setS({ ...s, google_site_verification: e.target.value })} data-testid="settings-google-verification" className="mt-1.5 h-11 font-mono-num text-xs" placeholder="e.g. google-site-verification code (content='...')" />
          <p className="text-[11px] text-slate-400 mt-1">Search Console me HTML tag method chunein aur jo code mile use yahan paste karein.</p>
        </div>
        <div>
          <Label className="font-semibold text-slate-600">Google Analytics (GA4) Measurement ID</Label>
          <Input value={s.google_analytics_id || ""} onChange={(e) => setS({ ...s, google_analytics_id: e.target.value })} data-testid="settings-google-analytics" className="mt-1.5 h-11 font-mono-num text-xs" placeholder="e.g. G-XXXXXXXXXX" />
          <p className="text-[11px] text-slate-400 mt-1">Google Analytics property ka Measurement ID (G- se shuru hota hai).</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <h3 className="font-display font-bold text-[#0B223D]">Website</h3>
        <div className="flex items-center justify-between bg-rose-50 border border-rose-100 rounded-xl p-4">
          <div><p className="font-semibold text-[#0B223D]">Under Maintenance mode</p><p className="text-sm text-slate-500">ON karte hi public website "Under Maintenance" dikhayegi. Admin panel chalta rahega.</p></div>
          <Switch checked={!!s.maintenance_mode} onCheckedChange={(v) => setS({ ...s, maintenance_mode: v })} data-testid="settings-maintenance-mode" />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-[#0B223D]">Social Media Handles (footer me icons dikhenge)</h3>
        {[["social_instagram", "Instagram URL"], ["social_facebook", "Facebook URL"], ["social_twitter", "X / Twitter URL"], ["social_linkedin", "LinkedIn URL"], ["social_youtube", "YouTube URL"], ["social_telegram", "Telegram URL"]].map(([k, l]) => (
          <div key={k}><Label className="font-semibold text-slate-600">{l}</Label><Input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} className="mt-1.5 h-11" placeholder="https://…" /></div>
        ))}
      </div>
      <Button onClick={save} data-testid="settings-save-btn" className="bg-[#0B223D] text-white font-bold h-11 rounded-xl px-6">Save all settings</Button>
    </div>
  );
}

function Audience() {
  const [data, setData] = useState({ count: 0, segments: [], distribution: {}, audience: [] });
  const [seg, setSeg] = useState("");
  const load = () => api.get(`/admin/audience${seg ? `?segment=${encodeURIComponent(seg)}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seg]);
  const exportXlsx = () => downloadFile(`${BACKEND_URL}/api/admin/audience/export.xlsx${seg ? `?segment=${encodeURIComponent(seg)}` : ""}`, "creditkendra_audience_export.xlsx");
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Audience &amp; Interest Segments</h1>
        <Button onClick={exportXlsx} data-testid="audience-export-btn" className="bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {Object.entries(data.distribution).map(([s, n]) => (
          <button key={s} onClick={() => setSeg(seg === s ? "" : s)} data-testid={`segment-chip-${s}`} className={`rounded-xl p-3 text-left border transition-colors ${seg === s ? "bg-teal-600 text-white border-teal-600" : "bg-white border-slate-200 hover:border-teal-400"}`}>
            <p className="text-xs font-semibold opacity-70">{s}</p><p className="font-mono-num font-bold text-lg">{n}</p>
          </button>
        ))}
      </div>
      <p className="text-sm text-slate-500">{data.count} visitor profile(s){seg && ` — interested in ${seg}`}</p>
      <Table head={["Visitor", "User ID", "Name", "Mobile", "Top Interest", "Events", "Source", "Last Seen"]} testid="admin-audience-table">
        {data.audience.map((a) => (
          <tr key={a.visitor_id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs text-slate-400">{a.visitor_id.slice(0, 8)}</td>
            <td className="p-3.5 font-mono-num text-xs">{a.user_id || "—"}</td>
            <td className="p-3.5 text-[#0B223D]">{a.name || "—"}</td>
            <td className="p-3.5 font-mono-num">{a.mobile || "—"}</td>
            <td className="p-3.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700">{a.top_segment}</span></td>
            <td className="p-3.5 font-mono-num text-slate-500">{a.events}</td>
            <td className="p-3.5 text-slate-500 text-xs">{a.last_source || "—"}</td>
            <td className="p-3.5 text-slate-400 text-xs">{a.last_seen ? new Date(a.last_seen).toLocaleDateString("en-IN") : "—"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function BulkEnquiries() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/bulk-enquiries").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Bulk Work Enquiries</h1>
      <Table head={["Date", "Name", "Mobile", "Product", "Traffic Source", "Volume", "City", "Message"]} testid="admin-bulk-table">
        {list.map((b) => (
          <tr key={b.id} className="border-t border-slate-100">
            <td className="p-3.5 text-slate-400 text-xs">{new Date(b.created_at).toLocaleDateString("en-IN")}</td>
            <td className="p-3.5 font-medium text-[#0B223D]">{b.name}</td>
            <td className="p-3.5 font-mono-num">{b.mobile}</td>
            <td className="p-3.5">{b.product}</td>
            <td className="p-3.5 text-slate-500 text-xs">{b.traffic_source}</td>
            <td className="p-3.5 text-slate-500">{b.monthly_volume || "—"}</td>
            <td className="p-3.5 text-slate-500">{b.city || "—"}</td>
            <td className="p-3.5 text-slate-500 text-xs max-w-[220px] truncate">{b.message || "—"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function LoginLinks() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/login-links").then((r) => setD(r.data)).catch(() => {}); }, []);
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Link copied!"); };
  if (!d) return null;
  const Card = ({ title, url, note, testid, accent }) => (
    <div className={`rounded-2xl p-6 border ${accent ? "bg-[#0B223D] text-white border-[#0B223D]" : "bg-white border-slate-200"}`}>
      <h3 className={`font-display font-bold ${accent ? "text-white" : "text-[#0B223D]"}`}>{title}</h3>
      <p className={`text-sm mt-1 ${accent ? "text-white/60" : "text-slate-400"}`}>{note}</p>
      <div className="flex gap-2 mt-3">
        <Input readOnly value={url} className={`h-11 font-mono-num text-xs ${accent ? "bg-white/10 border-white/20 text-white" : ""}`} data-testid={testid} />
        <Button onClick={() => copy(url)} data-testid={`${testid}-copy`} className={accent ? "bg-teal-500 h-11" : "bg-[#0B223D] text-white h-11"}><Copy className="w-4 h-4" /></Button>
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Login Links &amp; Roles</h1>
      <p className="text-sm text-slate-500">Super Admin aur Adviser ke login links alag hain. Team (Campaign Manager / Employee) shared link se login karti hai — access unke role par depend karta hai (role aap "Users" section me set karte ho).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card title={d.super_admin.label} url={d.super_admin.url} note={d.super_admin.note} testid="login-link-super" accent />
        <Card title={d.team.label} url={d.team.url} note={d.team.note} testid="login-link-team" />
        <Card title={d.adviser.label} url={d.adviser.url} note={d.adviser.note} testid="login-link-adviser" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-display font-bold text-[#0B223D] mb-3">Role mapping</h3>
        <div className="grid sm:grid-cols-4 gap-3">
          {Object.entries(d.roles).map(([k, v]) => (
            <div key={k} className="bg-slate-50 rounded-xl p-3"><p className="font-semibold text-[#0B223D]">{v}</p><p className="text-xs text-slate-400 font-mono-num">{k}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}


function PartnerTerms() {
  const [list, setList] = useState([]);
  const [acc, setAcc] = useState({ count: 0, acceptances: [] });
  const [tab, setTab] = useState("versions");
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = () => {
    api.get("/admin/terms").then((r) => setList(r.data)).catch(() => {});
    api.get("/admin/terms/acceptances").then((r) => setAcc(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const saveDraft = async () => {
    try {
      if (draft.id) await api.put(`/admin/terms/${draft.id}`, { title: draft.title, content: draft.content });
      else await api.post("/admin/terms", { title: draft.title, content: draft.content });
      toast.success("Terms version saved (draft)"); setDraft(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const publish = async (t) => {
    if (!window.confirm(`Publish v${t.version}? Naye partners ko ye version dikhega. Purana active version archive ho jayega (delete nahi hoga).`)) return;
    try { await api.post(`/admin/terms/${t.id}/publish`); toast.success(`v${t.version} published`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deact = async (t) => {
    if (!window.confirm(`Deactivate v${t.version}?`)) return;
    try { await api.post(`/admin/terms/${t.id}/deactivate`); toast.success("Deactivated (archived)"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const badge = (s) => s === "active" ? "bg-teal-50 text-teal-700" : s === "draft" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500";
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Partner Terms &amp; Conditions</h1>
          <p className="text-sm text-slate-500 mt-1">Versioned Partner Declaration &amp; Terms — ek hi waqt me sirf ek version Active hota hai. Purane versions aur acceptance records kabhi delete nahi hote.</p>
        </div>
        <Button onClick={() => setDraft({ id: null, title: "Partner Declaration & Terms", content: "" })} data-testid="terms-new-btn" className="bg-[#0B223D] text-white font-bold rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New Version</Button>
      </div>
      <div className="flex gap-2">
        {[["versions", `Versions (${list.length})`], ["acceptances", `Acceptance History (${acc.count})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`terms-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-[#0B223D] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "versions" && (
        <Table head={["Version", "Title", "Status", "Created", "Published", "Actions"]} testid="terms-table">
          {list.map((t) => (
            <tr key={t.id} className="border-t border-slate-100" data-testid={`terms-row-${t.version}`}>
              <td className="p-3.5 font-mono-num font-bold text-teal-700">v{t.version}</td>
              <td className="p-3.5 font-medium text-[#0B223D]">{t.title}</td>
              <td className="p-3.5"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${badge(t.status)}`}>{t.status}</span></td>
              <td className="p-3.5 text-xs text-slate-400">{(t.created_at || "").slice(0, 10)}<br />{t.created_by_name || "—"}</td>
              <td className="p-3.5 text-xs text-slate-400">{t.published_at ? t.published_at.slice(0, 10) : "—"}</td>
              <td className="p-3.5"><div className="flex gap-1">
                <button onClick={() => setPreview(t)} data-testid={`terms-preview-${t.version}`} title="Preview" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Eye className="w-4 h-4" /></button>
                {t.status === "draft" && <button onClick={() => setDraft({ id: t.id, title: t.title, content: t.content })} data-testid={`terms-edit-${t.version}`} title="Edit draft" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><SlidersHorizontal className="w-4 h-4" /></button>}
                {t.status !== "active" && <button onClick={() => publish(t)} data-testid={`terms-publish-${t.version}`} title="Publish" className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-600"><Play className="w-4 h-4" /></button>}
                {t.status === "active" && <button onClick={() => deact(t)} data-testid={`terms-deactivate-${t.version}`} title="Deactivate" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Pause className="w-4 h-4" /></button>}
              </div></td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "acceptances" && (
        <Table head={["Partner ID", "Partner Name", "Terms Version", "Accepted At", "IP", "Device"]} testid="terms-acceptances-table">
          {acc.acceptances.map((a) => (
            <tr key={a.id} className="border-t border-slate-100" data-testid={`acc-${a.partner_code}`}>
              <td className="p-3.5 font-mono-num text-xs font-bold text-teal-700">{a.partner_code}</td>
              <td className="p-3.5 font-medium text-[#0B223D]">{a.partner_name}</td>
              <td className="p-3.5 font-mono-num text-xs">v{a.terms_version ?? "—"}</td>
              <td className="p-3.5 text-xs text-slate-500 whitespace-nowrap">{(a.accepted_at || "").replace("T", " ").slice(0, 19)}</td>
              <td className="p-3.5 font-mono-num text-xs text-slate-400">{a.ip || "—"}</td>
              <td className="p-3.5 text-xs text-slate-400 max-w-[220px] truncate" title={a.user_agent}>{a.user_agent || "—"}</td>
            </tr>
          ))}
          {!acc.acceptances.length && <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-sm">Abhi koi acceptance record nahi.</td></tr>}
        </Table>
      )}

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent data-testid="terms-editor" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{draft?.id ? `Edit Draft v${list.find((t) => t.id === draft.id)?.version}` : "New Terms Version"}</DialogTitle></DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="terms-title" className="mt-1" /></div>
              <div><Label>Terms content</Label><Textarea rows={16} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} data-testid="terms-content" className="mt-1 font-mono-num text-xs" placeholder="Full terms text…" /></div>
              <p className="text-xs text-slate-400">Save hone par Draft rahega. Publish karne par hi naye partners ko dikhega.</p>
              <Button onClick={saveDraft} data-testid="terms-save-btn" className="w-full bg-[#0B223D] text-white font-bold">Save {draft.id ? "Draft" : "as Draft"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="terms-preview-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{preview?.title} (v{preview?.version})</DialogTitle></DialogHeader>
          <div className="whitespace-pre-line text-sm text-slate-600 leading-relaxed">{preview?.content}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function KnowledgeAdmin() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", category: "", body: "", active: true };
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/articles").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openEdit = (a) => { setEditing(a); setF({ title: a.title, category: a.category, body: a.body, active: a.active }); setOpen(true); };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title chahiye");
    try {
      if (editing) { await api.put(`/admin/articles/${editing.id}`, f); toast.success("Article updated"); }
      else { await api.post("/admin/articles", f); toast.success("Article added"); }
      setOpen(false); setEditing(null); setF(blank); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (a) => { if (!window.confirm(`Delete "${a.title}"?`)) return; try { await api.delete(`/admin/articles/${a.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggle = async (a) => { try { await api.put(`/admin/articles/${a.id}`, { title: a.title, category: a.category, body: a.body, active: !a.active }); load(); } catch (e) {} };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Knowledge Articles</h1>
          <p className="text-sm text-slate-500 mt-1">Knowledge page ke articles yahan se add/edit/delete karein.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setF(blank); } }}>
          <DialogTrigger asChild><Button className="bg-[#0B223D] text-white font-bold rounded-xl" data-testid="article-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Article</Button></DialogTrigger>
          <DialogContent data-testid="article-dialog" className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="article-title" className="mt-1" /></div>
              <div><Label className="text-xs">Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="article-category" className="mt-1" placeholder="e.g. Loans, Credit, Investing" /></div>
              <div><Label className="text-xs">Content (har line alag paragraph)</Label><Textarea rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="article-body" className="mt-1" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="article-active" /> Published (website par dikhe)</label>
              <Button onClick={save} data-testid="article-save-btn" className="w-full bg-[#0B223D] text-white font-bold">{editing ? "Save changes" : "Add article"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Table head={["Title", "Category", "Status", "Actions"]} testid="articles-table">
        {list.map((a) => (
          <tr key={a.id} className="border-t border-slate-100" data-testid={`article-row-${a.id}`}>
            <td className="p-3.5 font-medium text-[#0B223D]">{a.title}</td>
            <td className="p-3.5 text-slate-500 text-sm">{a.category}</td>
            <td className="p-3.5">
              <button onClick={() => toggle(a)} data-testid={`article-toggle-${a.id}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${a.active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>{a.active ? "Published" : "Hidden"}</button>
            </td>
            <td className="p-3.5"><div className="flex gap-1">
              <button onClick={() => openEdit(a)} data-testid={`article-edit-${a.id}`} title="Edit" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><SlidersHorizontal className="w-4 h-4" /></button>
              <button onClick={() => del(a)} data-testid={`article-delete-${a.id}`} title="Delete" className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500"><X className="w-4 h-4" /></button>
            </div></td>
          </tr>
        ))}
        {!list.length && <tr><td colSpan={4} className="p-8 text-center text-slate-400 text-sm">Koi article nahi — Add Article se banayein.</td></tr>}
      </Table>
    </div>
  );
}


function WalletLedger() {
  const [data, setData] = useState({ count: 0, transactions: [] });
  const [flt, setFlt] = useState({ q: "", type: "", status: "" });
  const [revTxn, setRevTxn] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const qs = () => Object.entries(flt).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const load = () => api.get(`/admin/wallet/transactions?${qs()}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const doReverse = async () => {
    if (reason.trim().length < 5) { toast.error("Reversal reason likhna zaroori hai (min 5 chars)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/wallet/${revTxn.txn_id}/reverse`, { reason: reason.trim() });
      if (data.status === "flagged_review") toast.success(`Balance kam hai — review ke liye flagged (${data.reversal_txn_id})`);
      else toast.success(`Reversed! Reversal ID: ${data.reversal_txn_id}`);
      setRevTxn(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const REVERSIBLE = ["referral_reward", "upstream_referral_reward", "milestone_reward", "signup_bonus", "adjustment"];
  const STATUS_CLS = { approved: "bg-teal-50 text-teal-700", pending: "bg-amber-50 text-amber-700", reversed: "bg-rose-50 text-rose-700", completed: "bg-slate-100 text-slate-600", flagged_review: "bg-orange-50 text-orange-700", paid: "bg-indigo-50 text-indigo-700" };
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Wallet Ledger</h1>
        <p className="text-sm text-slate-500 mt-1">Saare wallet transactions — rewards, withdrawals, reversals. Galat credit ko <b>Reverse Fund</b> se safely ultaya ja sakta hai (original record delete nahi hota).</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-2">
        <Input placeholder="Search: user code / txn ID / reference" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="wallet-filter-q" className="h-10 w-64" />
        <select value={flt.type} onChange={(e) => setFlt({ ...flt, type: e.target.value })} data-testid="wallet-filter-type" className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
          <option value="">All types</option><option value="referral_reward">Referral Reward</option><option value="upstream_referral_reward">Upstream Reward</option><option value="milestone_reward">Milestone</option><option value="signup_bonus">Signup Bonus</option><option value="withdrawal">Withdrawal</option><option value="reversal">Reversal</option>
        </select>
        <select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} data-testid="wallet-filter-status" className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
          <option value="">All status</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="reversed">Reversed</option><option value="completed">Completed</option><option value="flagged_review">Flagged Review</option>
        </select>
        <Button onClick={load} data-testid="wallet-apply-filter" className="bg-[#0B223D] text-white h-10">Apply</Button>
      </div>
      <p className="text-sm text-slate-500">{data.count} transaction(s)</p>
      <Table head={["Txn ID", "User", "Type", "Amount", "Status", "Campaign", "Reference", "Date", "Action"]} testid="wallet-ledger-table">
        {data.transactions.map((t) => (
          <tr key={t.txn_id} className="border-t border-slate-100">
            <td className="p-3.5 font-mono-num text-xs font-bold text-teal-700">{t.txn_id}</td>
            <td className="p-3.5 text-sm font-medium text-[#0B223D]">{t.user_name}<div className="text-xs font-mono-num text-slate-400">{t.user_code}</div></td>
            <td className="p-3.5 text-xs text-slate-600">{t.type}</td>
            <td className={`p-3.5 font-mono-num font-bold ${t.amount < 0 ? "text-rose-600" : "text-teal-700"}`}>{t.amount < 0 ? "-" : "+"}{inr(Math.abs(t.amount))}</td>
            <td className="p-3.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_CLS[t.status] || "bg-slate-100 text-slate-600"}`}>{(t.status || "").replace("_", " ")}</span></td>
            <td className="p-3.5 text-xs text-slate-500">{t.campaign_name}</td>
            <td className="p-3.5 font-mono-num text-xs text-slate-400">{t.reference || "-"}</td>
            <td className="p-3.5 text-xs text-slate-400">{(t.created_at || "").slice(0, 10)}</td>
            <td className="p-3.5">
              {REVERSIBLE.includes(t.type) && t.amount > 0 && t.status === "approved" && !t.already_reversed
                ? <Button size="sm" variant="outline" onClick={() => { setRevTxn(t); setReason(""); }} data-testid={`reverse-btn-${t.txn_id}`} className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reverse Fund</Button>
                : t.type === "reversal" ? <span className="text-[10px] font-bold text-orange-600">REVERSAL of {t.meta?.original_txn_id}</span> : null}
            </td>
          </tr>
        ))}
      </Table>
      <Dialog open={!!revTxn} onOpenChange={(o) => !o && setRevTxn(null)}>
        <DialogContent data-testid="reverse-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Reverse Fund — {revTxn?.txn_id}</DialogTitle></DialogHeader>
          {revTxn && (
            <div className="space-y-3">
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm space-y-1">
                <p><b>User:</b> {revTxn.user_name} ({revTxn.user_code})</p>
                <p><b>Amount:</b> <span className="font-mono-num font-bold text-rose-700">{inr(revTxn.amount)}</span></p>
                <p><b>Type:</b> {revTxn.type} · <b>Campaign:</b> {revTxn.campaign_name}</p>
                <p><b>Reference:</b> <span className="font-mono-num text-xs">{revTxn.reference || "-"}</span> · <b>Date:</b> {(revTxn.created_at || "").slice(0, 10)}</p>
              </div>
              <p className="text-xs text-slate-500">Original transaction <b>delete nahi hogi</b> — wo "Reversed" mark hogi aur ek linked reversal entry banegi. Agar user ka balance insufficient hai (funds withdraw ho chuke), to reversal <b>review ke liye flag</b> hoga, wallet negative nahi hoga.</p>
              <div>
                <Label className="text-xs font-semibold">Reversal reason (required)</Label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate lead par galti se credit ho gaya" data-testid="reverse-reason-input" className="mt-1" />
              </div>
              <Button onClick={doReverse} disabled={busy} data-testid="reverse-confirm-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold">
                {busy ? "Processing…" : `Confirm — Reverse ${inr(revTxn.amount)}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const SECTIONS = [
  ["dashboard", "Dashboard", LayoutDashboard, Dashboard, ["admin", "manager", "staff"], "Overview"],
  ["funnel", "Funnel", TrendingUp, Funnel, ["admin", "manager"], "Overview"],
  ["reports", "Reports", BarChart3, Reports, ["admin", "manager"], "Overview"],
  ["products", "Products", Package, Products, ["admin", "manager"], "Campaigns"],
  ["campaigns", "Campaigns", Megaphone, Campaigns, ["admin", "manager", "staff"], "Campaigns"],
  ["forms", "Form Builder", FormInput, FormBuilder, ["admin", "manager"], "Campaigns"],
  ["popups", "Offer Popups", ImageIcon, Popups, ["admin", "manager"], "Campaigns"],
  ["collected", "Collected Data", Database, CollectedData, ["admin", "manager", "staff"], "Leads & Data"],
  ["submissions", "Applications", FileStack, Submissions, ["admin", "manager", "staff"], "Leads & Data"],
  ["audience", "Audience", Target, Audience, ["admin", "manager", "staff"], "Leads & Data"],
  ["bulk", "Bulk Enquiries", Briefcase, BulkEnquiries, ["admin", "manager", "staff"], "Leads & Data"],
  ["postbacks", "Postback Logs", Repeat, PostbackLogs, ["admin", "manager", "staff"], "Leads & Data"],
  ["users", "Users", Users2, UsersAdmin, ["admin", "manager"], "People"],
  ["network-tree", "Network Tree", NetworkIcon, NetworkTree, ["admin", "manager"], "People"],
  ["kyc", "KYC Queue", ShieldCheck, KYCQueue, ["admin", "manager"], "People"],
  ["kyc-reminders", "KYC Reminders", Mail, KycReminders, ["admin", "manager"], "People"],
  ["loginlinks", "Login Links", KeyRound, LoginLinks, ["admin"], "People"],
  ["withdrawals", "Withdrawals", ArrowDownToLine, Withdrawals, ["admin", "manager"], "Finance"],
  ["wallet-ledger", "Wallet Ledger", Wallet, WalletLedger, ["admin"], "Finance"],
  ["knowledge", "Knowledge", ScrollText, KnowledgeAdmin, ["admin", "manager"], "Content"],
  ["terms", "Partner Terms", FileText, PartnerTerms, ["admin", "manager"], "Content"],
  ["feedback", "Feedback", Star, Feedback, ["admin", "manager"], "Content"],
  ["access", "Access Matrix", SlidersHorizontal, AccessMatrix, ["admin", "manager"], "System"],
  ["audit", "Audit Logs", ScrollText, Audit, ["admin", "manager"], "System"],
  ["settings", "Settings", SettingsIcon, Settings, ["admin"], "System"],
  ["my-account", "My Account", UserCog, MyAccount, ["admin", "manager", "staff"], "System"],
];

export default function AdminConsole() {
  const { user, logout, exitImpersonation } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const allowed = SECTIONS.filter((s) => s[4].includes(user.role));
  const Current = (allowed.find((s) => s[0] === active) || allowed[0])[3];
  const exitImp = async () => {
    try { await exitImpersonation(); toast.success("Wapas Super Admin account me aa gaye"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const SidebarInner = () => (
    <>
      <div className="p-5"><Logo height={34} /></div>
      <div className="px-5 pb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-brand">{user.role} Console</span>
      </div>
      <nav className="px-3 space-y-0.5 flex-1 overflow-y-auto no-scrollbar">
        {(() => {
          const groups = [];
          allowed.forEach((s) => {
            const g = s[5] || "Other";
            let grp = groups.find((x) => x.name === g);
            if (!grp) { grp = { name: g, items: [] }; groups.push(grp); }
            grp.items.push(s);
          });
          const activeGroup = (allowed.find((s) => s[0] === active) || [])[5];
          const effectiveOpen = openGroup ?? activeGroup;
          return groups.map((g) => {
            const isOpen = g.name === effectiveOpen;
            return (
              <div key={g.name}>
                <button onClick={() => setOpenGroup(g.name === effectiveOpen && g.name !== activeGroup ? null : g.name)}
                  data-testid={`admin-nav-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="w-full flex items-center justify-between px-3 pt-3.5 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">
                  {g.name} {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                {isOpen && g.items.map(([key, label, Icon]) => (
                  <button key={key} onClick={() => { setActive(key); setOpenGroup(null); setOpen(false); }} data-testid={`admin-nav-${key}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${active === key ? "bg-[#0B223D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                    <Icon style={{ width: 18, height: 18 }} /> {label}
                  </button>
                ))}
              </div>
            );
          });
        })()}
      </nav>
      <div className="p-3 border-t border-slate-200 space-y-1">
        <button onClick={() => nav("/")} className="w-full text-left px-3 py-2 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-100">View website</button>
        <button onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {user.impersonating && (
        <div data-testid="impersonation-banner" className="bg-amber-400 text-[#0B223D] px-4 py-2.5 flex items-center justify-between gap-3 text-sm font-semibold sticky top-0 z-40">
          <span className="truncate min-w-0">Aap <b>{user.name}</b> ({user.user_code}) ke account me hain</span>
          <button onClick={exitImp} data-testid="impersonation-exit-btn" className="shrink-0 bg-[#0B223D] text-white px-3.5 py-1.5 rounded-lg text-xs font-bold hover:bg-[#06182B] transition-colors">Exit — back to Super Admin</button>
        </div>
      )}
      <div className="flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200 h-screen sticky top-0"><SidebarInner /></aside>
      {open && <div className="lg:hidden fixed inset-0 z-50 flex"><div className="w-64 bg-white flex flex-col h-full"><SidebarInner /></div><div className="flex-1 bg-black/40" onClick={() => setOpen(false)} /></div>}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 glass bg-white/85 border-b border-slate-200 h-16 flex items-center px-4 lg:hidden">
          <button onClick={() => setOpen(true)}><Menu /></button>
        </header>
        <main className="p-4 sm:p-8"><Current /></main>
      </div>
      </div>
    </div>
  );
}
